import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import grapesjs, { Editor } from 'grapesjs';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import 'grapesjs/dist/css/grapes.min.css';
import gjsPresetWebpage from 'grapesjs-preset-webpage';
import gjsBlocksBasic from 'grapesjs-blocks-basic';
import gjsPluginForms from 'grapesjs-plugin-forms';
import gjsStyleBg from 'grapesjs-style-bg';
import { Sparkles, PanelLeftOpen, PanelRightOpen, X } from 'lucide-react';

import PageBuilderToolbar from '../../components/page-builder/core/PageBuilderToolbar';
import PublishModal from '../../components/page-builder/core/PublishModal';
import TemplateGallery from '../../components/page-builder/core/TemplateGallery';
import AIAssistant from '../../components/page-builder/core/AIAssistant';
import SaveTemplateModal from '../../components/page-builder/core/SaveTemplateModal';
import CustomCodeModal from '../../components/page-builder/core/CustomCodeModal';
import FullHtmlModal from '../../components/page-builder/core/FullHtmlModal';
import AIEditChat from '../../components/page-builder/core/AIEditChat';
import PageSettingsModal from '../../components/page-builder/core/PageSettingsModal';
import ImageStudioModal from '../../components/image-studio/ImageStudioModal';
import { registerCustomBlocks } from '../../components/page-builder/blocks';
import { LEAD_FORM_CARD_HTML } from '../../components/page-builder/blocks/conversionBlocks';
import { registerEmbedComponent } from '../../components/page-builder/core/customComponents';
import {
  applyFriendlyNames,
  enableResizeHandles,
  autoFixResponsive,
  clearResponsiveFix,
  hasResponsiveFix,
  detectExternalForms,
  collectScrollTargets,
  primaryFormId,
  translateDefaultBlocks,
  expandTopLayers,
  enableDeviceScopedStyles,
  stripFixFromHtmlHead,
  detectBrokenAnchors,
  applyImageSrc,
} from '../../components/page-builder/core/editorUx';
import { LandingPage, PageTemplate } from '../../types/pageBuilder';
// Template translation is now handled in TemplateGallery via getTranslatedTemplates
import { saveUserTemplate } from '../../services/userTemplateService';
import landingPageService from '../../services/landingPageService';
import api from '../../services/api';

// Documento HTML completo (começa com <!doctype html> ou <html>). Espelha o
// isFullHtmlDocument do backend — páginas assim são editadas/publicadas inteiras.
// Classes de lixo de colagem do Word/Google Docs. Elas trazem CSS próprio que
// sobrepõe o painel de estilo (texto fica preto/bold apesar do painel dizer outra coisa).
const OFFICE_CLASS_RE = /\b(TextRun|NormalTextRun|OutlineElement|WACImageContainer|ListContainerWrapper|SpellingError|BCX\d)\b|SCXW\d|\bdocs-/i;

const sanitizeOfficeArtifacts = (editor: any): void => {
  try {
    // 1) tira as classes do Office dos componentes (o texto passa a herdar do pai)
    const all = editor.getWrapper?.()?.find?.('*') || [];
    all.forEach((c: any) => {
      const classes = c.getClasses?.() || [];
      classes.forEach((cls: string) => { if (OFFICE_CLASS_RE.test(cls)) c.removeClass(cls); });
    });
    // 2) remove as regras CSS do Office que forçavam cor/peso
    const css = editor.Css;
    (css?.getRules?.() || []).forEach((r: any) => {
      const sel = r.selectorsToString?.() || r.getSelectorsString?.() || '';
      if (OFFICE_CLASS_RE.test(sel)) css.remove(r);
    });
  } catch (e) {
    console.warn('[sanitizeOffice] falhou:', e);
  }
};

const isFullHtmlDocument = (html: string): boolean =>
  !!html && /^\s*(<!doctype\s+html|<html[\s>])/i.test(html);

// Reconstrói o documento completo a partir das partes (páginas antigas,
// importadas antes desta feature, ficavam com head/body/css separados).
const reconstructFullHtml = (opts: {
  html: string; css: string; headCode: string; bodyEndCode: string; title: string;
}): string => `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${opts.title || 'Landing Page'}</title>
${opts.headCode || ''}
  <style>
${opts.css || ''}
  </style>
</head>
<body>
${opts.html || ''}
${opts.bodyEndCode || ''}
</body>
</html>`;

// Divide um documento HTML em <head>, corpo (sem scripts) e os <script> do body.
// Usado para carregar uma página HTML no editor visual (GrapesJS edita o corpo)
// preservando head/scripts para reanexar ao salvar.
const splitHtmlDoc = (fullHtml: string): { head: string; body: string; scripts: string } => {
  try {
    const doc = new DOMParser().parseFromString(fullHtml || '', 'text/html');
    const head = doc.head?.innerHTML || '';
    const scriptEls = Array.from(doc.body?.querySelectorAll('script') || []);
    const scripts = scriptEls.map(s => s.outerHTML).join('\n');
    scriptEls.forEach(s => s.remove());
    const body = doc.body?.innerHTML ?? (fullHtml || '');
    return { head, body, scripts };
  } catch {
    return { head: '', body: fullHtml || '', scripts: '' };
  }
};

// Reconstrói um documento HTML completo a partir do head/scripts preservados +
// o corpo/CSS que o editor visual produziu. Garante charset/viewport/Tailwind.
const buildFullDoc = (opts: { head: string; body: string; css: string; scripts: string; title: string }): string => {
  const head = opts.head || '';
  // Preserva o <head> EXATAMENTE como estava (fontes, CSS custom, Tailwind se
  // houver). NÃO injeta Tailwind nem resets — isso alteraria o design de páginas
  // que não usam Tailwind. Só completa charset/viewport/title se faltarem.
  const extra = [
    /charset/i.test(head) ? '' : '<meta charset="UTF-8">',
    /name=["']?viewport/i.test(head) ? '' : '<meta name="viewport" content="width=device-width, initial-scale=1.0">',
    /<title[\s>]/i.test(head) ? '' : `<title>${opts.title || 'Landing Page'}</title>`,
  ].filter(Boolean).join('\n  ');
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  ${extra ? extra + '\n' : ''}${head}
${opts.css ? `  <style>\n${opts.css}\n  </style>` : ''}
</head>
<body>
${opts.body || ''}
${opts.scripts || ''}
</body>
</html>`;
};

/**
 * Copia para a área de transferência e diz se REALMENTE copiou.
 * `navigator.clipboard` não existe fora de contexto seguro e pode ser negado
 * pelo navegador; o caminho antigo prometia "copiado!" sem checar nada.
 */
const copiarTexto = async (texto: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch { /* cai no fallback */ }
  try {
    const area = document.createElement('textarea');
    area.value = texto;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
};

const LandingPageBuilder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;
  const templateParam = searchParams.get('template');

  // Editor state
  const editorRef = useRef<Editor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  // Espelho em ref: o effect que carrega a página do servidor roda com um
  // closure antigo, onde isEditorReady ainda é false. Sem o ref, uma API lenta
  // fazia a página abrir VAZIA no editor (o conteúdo pendente nunca era aplicado).
  const isEditorReadyRef = useRef(false);

  // Page state
  const [pageId, setPageId] = useState<string | null>(id || null);
  const [pageName, setPageName] = useState(isEditing ? t('pageBuilder.loading') : t('pageBuilder.newPage'));
  const [pageSlug, setPageSlug] = useState('nova-landing-page');
  const [pageData, setPageData] = useState<Partial<LandingPage> | null>(null);
  const [isLoading, setIsLoading] = useState(isEditing);

  // Responsive: detect desktop vs mobile
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(templateParam === 'templates');
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showCustomCodeModal, setShowCustomCodeModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [activeDevice, setActiveDevice] = useState<'Desktop' | 'Tablet' | 'Mobile'>('Desktop');
  // Ajuste automático de responsividade (celular/tablet)
  const [isFixingResponsive, setIsFixingResponsive] = useState(false);
  const [responsiveFixApplied, setResponsiveFixApplied] = useState(false);
  // Aviso de formulário externo (RD Station e afins) que não funciona publicado
  const [externalFormWarning, setExternalFormWarning] = useState<{ provider: string; reason: string } | null>(null);
  const [externalFormDismissed, setExternalFormDismissed] = useState(false);
  const [leftPanel, setLeftPanel] = useState<'blocks' | 'layers'>('blocks');
  const [blockSearch, setBlockSearch] = useState('');

  // Filter GrapesJS blocks by label as the user types.
  // We walk the DOM that grapesjs renders inside #gjs-blocks and toggle
  // display on each block + its parent category (hide empty categories).
  useEffect(() => {
    const blocksEl = document.getElementById('gjs-blocks');
    if (!blocksEl) return;
    const q = blockSearch.trim().toLowerCase();

    const blockNodes = Array.from(blocksEl.querySelectorAll('.gjs-block')) as HTMLElement[];
    blockNodes.forEach((node) => {
      const label = ((node.querySelector('.gjs-block-label') as HTMLElement | null)?.textContent || '').trim().toLowerCase();
      const match = !q || label.includes(q);
      node.style.display = match ? '' : 'none';
    });

    // Hide categories that have no visible blocks. Also auto-open categories
    // with matches so search results are instantly visible.
    const categories = Array.from(blocksEl.querySelectorAll('.gjs-block-category')) as HTMLElement[];
    categories.forEach((cat) => {
      const visible = (Array.from(cat.querySelectorAll('.gjs-block')) as HTMLElement[])
        .some((n) => n.style.display !== 'none');
      cat.style.display = visible ? '' : 'none';
      if (q && visible) {
        cat.classList.add('gjs-open');
      }
    });
  }, [blockSearch, leftPanel]);
  // Ao abrir a aba Camadas, a árvore já vem aberta nos primeiros níveis
  // (antes aparecia só "Body" fechado e ninguém achava as seções).
  useEffect(() => {
    if (leftPanel !== 'layers' || !editorRef.current) return;
    const timer = setTimeout(() => {
      if (editorRef.current) expandTopLayers(editorRef.current);
    }, 120);
    return () => clearTimeout(timer);
  }, [leftPanel, isEditorReady]);

  const [rightPanel, setRightPanel] = useState<'styles' | 'traits'>('styles');
  const [showLeftSidebar, setShowLeftSidebar] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [showRightSidebar, setShowRightSidebar] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);

  // Allow grapesjs components (e.g. custom-embed) to request a right-panel
  // tab switch via a window event — so the "Editar código HTML" button in
  // Configurações is instantly visible when the embed is selected.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === 'styles' || detail === 'traits') {
        setRightPanel(detail);
        setShowRightSidebar(true);
      }
    };
    window.addEventListener('gjs-switch-right-panel', handler as EventListener);
    return () => window.removeEventListener('gjs-switch-right-panel', handler as EventListener);
  }, []);

  // Custom code state
  const [customCode, setCustomCode] = useState({
    headCode: '',
    bodyStartCode: '',
    bodyEndCode: '',
    customCss: ''
  });

  // Template thumbnail (from selected template)
  const [pageThumbnail, setPageThumbnail] = useState<string | null>(null);

  // Imported HTML mode: pages uploaded via "Import HTML" bypass GrapesJS so
  // their original markup/styles/scripts are preserved exactly. We render
  // them in an iframe and save without touching the editor.
  const [isImportedHtml, setIsImportedHtml] = useState(false);
  const [importedHtml, setImportedHtml] = useState('');
  const [importedCss, setImportedCss] = useState('');
  // HTML COMPLETO das páginas importadas: o usuário edita o documento inteiro
  // (<!DOCTYPE html>...</html>) e é isso que é salvo em gjs_html e publicado
  // verbatim. Substitui a edição por partes (head/body separados).
  const [fullHtml, setFullHtml] = useState('');
  const [showFullHtmlModal, setShowFullHtmlModal] = useState(false);
  // Chat "Editar com IA" + histórico de HTML para o Desfazer (undo client-side).
  const [showAIEditChat, setShowAIEditChat] = useState(false);
  const [htmlUndoStack, setHtmlUndoStack] = useState<string[]>([]);
  // Páginas HTML são editadas VISUALMENTE no GrapesJS (corpo no canvas) e salvas
  // como documento completo. O <head> e os <script> do body são preservados aqui
  // para reanexar na reconstrução. GrapesJS é a fonte da verdade nesse modo.
  const importedHeadRef = useRef('');
  const importedScriptsRef = useRef('');

  // Image change modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [showImageStudio, setShowImageStudio] = useState(false);
  const [selectedImageComponent, setSelectedImageComponent] = useState<any>(null);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // Banco de imagens: tudo que já foi enviado por este usuário fica salvo e
  // reaparece aqui (antes a imagem "sumia" e era preciso subir de novo).
  const [imageLibrary, setImageLibrary] = useState<{ url: string; key: string; name: string }[]>([]);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState(false);
  const [deletingImageKey, setDeletingImageKey] = useState<string | null>(null);
  // Muda a cada abertura/fechamento do modal. Um upload lento que terminava
  // depois de o usuário cancelar (ou abrir outra imagem) aplicava a foto no
  // componente errado e fechava o modal na cara dele — comparamos a "rodada".
  const imageModalSeqRef = useRef(0);

  // Responsive resize listener
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize GrapesJS
  useEffect(() => {
    if (!containerRef.current || editorRef.current) return;

    const editor = grapesjs.init({
      container: containerRef.current,
      height: '100%',
      width: 'auto',
      fromElement: false,
      // Aplica estilos do painel INLINE no elemento (maior especificidade). Templates
      // importados trazem regras por id + lixo do Word que venciam a regra do editor,
      // fazendo a troca de cor "não pegar". Inline garante que a edição sempre vale.
      // @ts-ignore - avoidInlineStyle é válido em runtime, fora dos tipos do GrapesJS
      avoidInlineStyle: false,

      storageManager: {
        type: 'none',
        autosave: false
      },

      // @ts-ignore - GrapesJS types don't include assetManager but it works
      assetManager: {
        uploadFile: async (e: any) => {
          const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
          const token = localStorage.getItem('token');
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

          // Este caminho (arrastar arquivo para o canvas) não avisava NADA quando
          // dava errado: arquivo grande ou não-imagem simplesmente sumia e o
          // usuário repetia o gesto achando que o arraste não tinha pegado.
          for (let i = 0; i < files.length; i++) {
            const f = files[i];
            const tipo = (f.type || '').toLowerCase();
            if (/svg|heic|heif/.test(tipo) || /\.(svg|heic|heif)$/i.test(f.name)) {
              toast.error(`${f.name}: formato não aceito. Envie PNG, JPG, GIF ou WEBP.`);
              continue;
            }
            if (!tipo.startsWith('image/')) {
              toast.error(`${f.name}: não é uma imagem.`);
              continue;
            }
            if (f.size === 0) {
              toast.error(`${f.name}: arquivo vazio (0 KB).`);
              continue;
            }
            if (f.size > 10 * 1024 * 1024) {
              toast.error(`${f.name}: imagem muito grande. O limite é 10MB.`);
              continue;
            }
            try {
              const formData = new FormData();
              formData.append('file', f);
              const res = await fetch(`${API_URL}/upload/image`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
              });
              const data = await res.json();
              if (data.success && data.url) {
                (editor as any).AssetManager.add({ src: data.url, type: 'image' });
              } else {
                toast.error(data.message || `Não consegui enviar ${f.name}.`);
              }
            } catch (err) {
              console.error('Asset upload failed:', err);
              toast.error(`Falha ao enviar ${f.name}. Verifique sua conexão.`);
            }
          }
        },
        autoAdd: true,
      },

      deviceManager: {
        devices: [
          { name: 'Desktop', width: '', widthMedia: '' },
          { name: 'Tablet', width: '768px', widthMedia: '992px' },
          { name: 'Mobile', width: '375px', widthMedia: '768px' }
        ]
      },

      canvas: {
        styles: [
          'https://cdn.jsdelivr.net/npm/tailwindcss@3.4.1/dist/tailwind.min.css',
          'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap'
        ],
        scripts: [
          'https://cdn.tailwindcss.com'
        ]
      },

      plugins: [
        gjsPresetWebpage,
        gjsBlocksBasic,
        gjsPluginForms,
        gjsStyleBg
      ],
      pluginsOpts: {
        [gjsPresetWebpage as any]: {
          modalImportTitle: 'Importar HTML',
          modalImportLabel: '<div style="margin-bottom: 10px;">Cole seu HTML aqui</div>',
          textCleanCanvas: 'Tem certeza que deseja limpar o canvas?',
          showStylesOnChange: false,
          blocksBasicOpts: false,
          formsOpts: false,
          navbarOpts: false,
          countdownOpts: false,
          exportOpts: false,
          aviaryOpts: false,
          filestackOpts: false,
          useCustomTheme: false
        },
        [gjsBlocksBasic as any]: {
          flexGrid: true,
          rowHeight: 75,
          category: 'Layout básico'
        },
        [gjsPluginForms as any]: {
          category: 'Formulários'
        },
        [gjsStyleBg as any]: {}
      },

      styleManager: {
        appendTo: '#gjs-sm',
        sectors: [
          {
            name: 'Texto',
            open: true,
            buildProps: ['font-family', 'font-size', 'font-weight', 'letter-spacing', 'color', 'line-height', 'text-align', 'text-decoration', 'text-transform'],
            properties: [
              { property: 'font-family', label: 'Fonte' },
              { property: 'font-size', label: 'Tamanho' },
              { property: 'font-weight', label: 'Peso' },
              { property: 'letter-spacing', label: 'Espaçamento' },
              { property: 'color', label: 'Cor' },
              { property: 'line-height', label: 'Altura da linha' },
              { property: 'text-align', label: 'Alinhamento' },
              { property: 'text-decoration', label: 'Decoração' },
              { property: 'text-transform', label: 'Transformar' },
              { property: 'text-shadow', label: 'Sombra do Texto', type: 'text', defaults: '' },
              {
                property: '-webkit-background-clip',
                label: 'Gradiente no Texto',
                type: 'select',
                defaults: 'border-box',
                info: 'Para texto gradiente: defina o gradiente em "Fundo e Gradiente", depois ative aqui',
                options: [
                  { id: 'border-box', label: 'Não' },
                  { id: 'text', label: 'Sim (ativar gradiente)' }
                ]
              },
              {
                property: '-webkit-text-fill-color',
                label: 'Preenchimento do Texto',
                type: 'select',
                defaults: 'inherit',
                options: [
                  { id: 'inherit', label: 'Normal' },
                  { id: 'transparent', label: 'Transparente (para gradiente)' }
                ]
              }
            ]
          },
          {
            name: 'Fundo e Gradiente',
            open: true,
            buildProps: ['background-color'],
            properties: [
              { property: 'background-color', label: 'Cor de Fundo' },
              {
                property: 'background-image',
                label: 'Gradiente / Imagem',
                type: 'text',
                defaults: '',
                info: 'Ex: linear-gradient(135deg, #667eea 0%, #764ba2 100%) ou url(...)'
              },
              { property: 'background-repeat', label: 'Repetir' },
              { property: 'background-position', label: 'Posição do Fundo' },
              { property: 'background-size', label: 'Tamanho do Fundo' }
            ]
          },
          {
            name: 'Imagem',
            open: false,
            properties: [
              {
                property: 'object-fit',
                label: 'Ajuste da Imagem',
                type: 'select',
                defaults: '',
                options: [
                  { id: '', label: 'Padrão' },
                  { id: 'cover', label: 'Cobrir' },
                  { id: 'contain', label: 'Conter' },
                  { id: 'fill', label: 'Preencher' },
                  { id: 'none', label: 'Nenhum' },
                  { id: 'scale-down', label: 'Reduzir' }
                ]
              },
              {
                property: 'object-position',
                label: 'Posição da Imagem',
                type: 'select',
                defaults: 'center center',
                options: [
                  { id: 'center center', label: 'Centro' },
                  { id: 'top center', label: 'Topo' },
                  { id: 'bottom center', label: 'Baixo' },
                  { id: 'left center', label: 'Esquerda' },
                  { id: 'right center', label: 'Direita' },
                  { id: 'top left', label: 'Topo Esquerda' },
                  { id: 'top right', label: 'Topo Direita' },
                  { id: 'bottom left', label: 'Baixo Esquerda' },
                  { id: 'bottom right', label: 'Baixo Direita' }
                ]
              },
              {
                property: 'aspect-ratio',
                label: 'Proporção',
                type: 'select',
                defaults: '',
                options: [
                  { id: '', label: 'Auto' },
                  { id: '1/1', label: '1:1 (Quadrado)' },
                  { id: '4/3', label: '4:3' },
                  { id: '16/9', label: '16:9' },
                  { id: '3/2', label: '3:2' },
                  { id: '2/3', label: '2:3 (Retrato)' },
                  { id: '9/16', label: '9:16 (Story)' }
                ]
              }
            ]
          },
          {
            name: 'Tamanho',
            open: false,
            buildProps: ['width', 'min-width', 'max-width', 'height', 'min-height', 'max-height', 'padding', 'margin']
          },
          {
            name: 'Borda',
            open: false,
            properties: [
              { property: 'border-radius', label: 'Arredondamento', type: 'composite',
                properties: [
                  { property: 'border-top-left-radius', label: '↖ Sup. Esq.' },
                  { property: 'border-top-right-radius', label: '↗ Sup. Dir.' },
                  { property: 'border-bottom-right-radius', label: '↘ Inf. Dir.' },
                  { property: 'border-bottom-left-radius', label: '↙ Inf. Esq.' }
                ]
              },
              {
                property: 'border-style',
                label: 'Estilo da Borda',
                type: 'select',
                defaults: 'none',
                options: [
                  { id: 'none', label: 'Nenhuma' },
                  { id: 'solid', label: 'Sólida' },
                  { id: 'dashed', label: 'Tracejada' },
                  { id: 'dotted', label: 'Pontilhada' },
                  { id: 'double', label: 'Dupla' },
                  { id: 'groove', label: 'Groove' },
                  { id: 'ridge', label: 'Ridge' },
                  { id: 'inset', label: 'Inset' },
                  { id: 'outset', label: 'Outset' }
                ]
              },
              { property: 'border-width', label: 'Largura da Borda' },
              { property: 'border-color', label: 'Cor da Borda' },
              { property: 'box-shadow', label: 'Sombra' },
              {
                property: 'clip-path',
                label: 'Forma (Clip)',
                type: 'select',
                defaults: '',
                options: [
                  { id: '', label: 'Nenhuma' },
                  { id: 'circle(50%)', label: 'Círculo' },
                  { id: 'ellipse(50% 40%)', label: 'Elipse' },
                  { id: 'inset(5% round 20px)', label: 'Retângulo arredondado' },
                  { id: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)', label: 'Pentágono' },
                  { id: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', label: 'Estrela' },
                  { id: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)', label: 'Hexágono' },
                  { id: 'polygon(50% 0%, 100% 100%, 0% 100%)', label: 'Triângulo' },
                  { id: 'polygon(0% 15%, 15% 15%, 15% 0%, 85% 0%, 85% 15%, 100% 15%, 100% 85%, 85% 85%, 85% 100%, 15% 100%, 15% 85%, 0% 85%)', label: 'Cruz' }
                ]
              }
            ]
          },
          {
            name: 'Aparência',
            open: false,
            buildProps: ['overflow', 'cursor'],
            properties: [
              {
                type: 'slider',
                property: 'opacity',
                label: 'Opacidade',
                defaults: '1',
                step: 0.01,
                max: 1,
                min: 0
              }
            ]
          },
          {
            name: 'Layout',
            open: false,
            buildProps: ['display', 'flex-direction', 'justify-content', 'align-items', 'flex-wrap', 'gap', 'position', 'top', 'right', 'bottom', 'left', 'z-index']
          },
          {
            name: 'Transformação',
            open: false,
            buildProps: ['transform', 'transition']
          }
        ]
      },

      panels: { defaults: [] },
      blockManager: { appendTo: '#gjs-blocks' },
      layerManager: { appendTo: '#gjs-layers' },
      selectorManager: { appendTo: '#gjs-selectors', componentFirst: true } as any,
      traitManager: { appendTo: '#gjs-traits' }
    });

    // Register ONLY the embed component type (not the full custom components
    // set, which would override image/button/section behavior and break existing
    // drag-and-drop). Must run BEFORE blocks since the embed block references
    // the 'custom-embed' type.
    registerEmbedComponent(editor);

    // Register custom blocks
    registerCustomBlocks(editor);

    // UX para quem não é desenvolvedor:
    // - Camadas com nomes em português ("Seção", "Botão: Garanta sua vaga");
    // - alças de redimensionamento em qualquer bloco (os "pontos de regulagem"
    //   que o cliente não encontrava) — largura em %, altura em min-height e
    //   sempre gravadas na media query do dispositivo ativo.
    applyFriendlyNames(editor);
    enableResizeHandles(editor);
    // Blocos dos plugins ("1 Column", "Checkbox"...) em português.
    translateDefaultBlocks(editor);
    // Ajuste no celular não pode desalinhar o desktop (e vice-versa).
    enableDeviceScopedStyles(editor);

    // "Puxei a alça para cima e não diminui mais": um bloco nunca fica menor
    // que o próprio conteúdo. Em vez de deixar o cliente achando que travou,
    // explicamos o caminho (espaçamento interno / tamanho do texto).
    let ultimoAvisoAltura = 0;
    editor.on('component:resize:end' as any, () => {
      try {
        const comp: any = editor.getSelected();
        const el: HTMLElement | undefined = comp?.getEl?.();
        if (!el) return;
        const win = el.ownerDocument?.defaultView;
        if (!win) return;
        const cs = win.getComputedStyle(el);
        const minH = parseFloat(cs.minHeight) || 0;
        const altura = el.getBoundingClientRect().height;
        if (minH > 0 && altura > minH + 6 && Date.now() - ultimoAvisoAltura > 20000) {
          ultimoAvisoAltura = Date.now();
          toast(
            'Este bloco não fica menor que o conteúdo dentro dele. Para diminuir: reduza o espaçamento em Estilos → Tamanho (padding) ou o tamanho do texto.',
            { duration: 8000, icon: 'ℹ️' }
          );
        }
      } catch { /* ignore */ }
    });

    // Duplo clique em imagens abre o modal de troca
    editor.on('component:dblclick', (component: any) => {
      const tagName = component.get('tagName')?.toLowerCase();
      const type = component.get('type');
      if (tagName === 'img' || type === 'image') {
        const currentSrc = component.getAttributes().src || '';
        setNewImageUrl(currentSrc);
        setSelectedImageComponent(component);
        imageModalSeqRef.current += 1;
        setShowImageModal(true);
        return false;
      }
    });


    // Auto-switch to styles panel when selecting a component (except custom-embed,
    // which wants the Configurações/traits tab so users see the "Editar código HTML" button).
    editor.on('component:selected', (component: any) => {
      const isEmbed = component?.get?.('type') === 'custom-embed'
        || component?.getAttributes?.()?.['data-gjs-type'] === 'custom-embed';
      if (isEmbed) {
        setRightPanel('traits');
        // Skip the generic ID / button-action / link traits — the embed has its
        // own focused set of traits (Editar HTML, Altura, Alinhamento, Largura).
        return;
      }

      // Formulário: traits próprios de pós-envio (mensagem de sucesso / redirect
      // pra página de obrigado). Os traits genéricos do plugin (method/action) e
      // os de ação de botão não fazem sentido aqui e só confundem o cliente.
      if ((component?.get?.('tagName') || '').toLowerCase() === 'form') {
        const formTraits = component.get('traits');
        if (formTraits) {
          ['method', 'action', 'data-action', 'data-href', 'data-target'].forEach((name) => {
            const existing = formTraits.where({ name });
            if (existing.length) formTraits.remove(existing[0]);
          });
          // Tirar o trait não tira o atributo: o plugin de forms deixa method="get"
          // no modelo, e se o runtime da DevSkin não carregar o browser faz submit
          // nativo — os dados do lead iriam parar na barra de endereço.
          if (typeof component.removeAttributes === 'function') {
            component.removeAttributes(['method', 'action']);
          }
          const ensureFormTrait = (traitDef: any) => {
            if (!formTraits.where({ name: traitDef.name }).length) formTraits.add(traitDef);
          };
          // Mantém o ID: é o que alimenta o seletor "Ir para Seção" dos botões.
          ensureFormTrait({
            type: 'text',
            name: 'id',
            label: 'ID do Elemento',
            placeholder: 'ex: form-contato',
            changeProp: false,
          });
          ensureFormTrait({
            type: 'text',
            name: 'data-success-message',
            label: 'Mensagem de Sucesso',
            placeholder: 'Seus dados foram enviados com sucesso! Entraremos em contato em breve.',
            changeProp: false,
          });
          ensureFormTrait({
            type: 'text',
            name: 'data-redirect-url',
            label: 'Redirecionar após envio',
            placeholder: 'Opcional — ex: /obrigado',
            changeProp: false,
          });
        }
        setRightPanel('traits');
        return;
      }

      setRightPanel('styles');

      // Build dynamic button action traits based on selected action
      const traits = component.get('traits');
      const currentAction = component.getAttributes()['data-action'] || '';

      // Always ensure component ID trait exists (for scroll-to-section targets)
      if (traits && !traits.where({ name: 'id' }).length) {
        traits.add({
          type: 'text',
          name: 'id',
          label: 'ID do Elemento',
          placeholder: 'ex: contato, precos',
          changeProp: false
        });
      }

      // Always ensure the action selector trait exists
      if (traits && !traits.where({ name: 'data-action' }).length) {
        traits.add({
          type: 'select',
          name: 'data-action',
          label: 'Ação do Botão',
          options: [
            { id: '', label: 'Nenhuma' },
            { id: 'link', label: 'Abrir Link' },
            { id: 'scroll', label: 'Ir para o formulário / seção' },
            { id: 'form', label: 'Enviar Formulário' },
            { id: 'whatsapp', label: 'Abrir WhatsApp' },
            { id: 'phone', label: 'Ligar (Telefone)' },
            { id: 'email', label: 'Enviar E-mail' }
          ],
          changeProp: false
        });
      }

      // Helper to add trait if missing
      const addTraitIfMissing = (traitDef: any) => {
        if (!traits.where({ name: traitDef.name }).length) {
          traits.add(traitDef);
        }
      };

      // Helper to remove trait if exists
      const removeTrait = (name: string) => {
        const existing = traits.where({ name });
        if (existing.length) traits.remove(existing[0]);
      };

      // Remove all action-specific traits first
      ['data-href', 'data-target', 'data-scroll-to', 'data-phone', 'data-email', 'data-email-subject', 'data-whatsapp', 'data-whatsapp-msg'].forEach(removeTrait);

      // Add relevant traits based on action type
      // Also add link traits if no action set but data-href exists (backward compat)
      const effectiveAction = currentAction || (component.getAttributes()['data-href'] ? 'link' : '');

      if (effectiveAction === 'link' || effectiveAction === '') {
        addTraitIfMissing({ type: 'text', name: 'data-href', label: 'URL do Link', placeholder: 'https://...', changeProp: false });
        addTraitIfMissing({ type: 'select', name: 'data-target', label: 'Abrir link em', options: [{ id: '', label: 'Mesma aba' }, { id: '_blank', label: 'Nova aba' }], changeProp: false });
      }
      if (effectiveAction === 'scroll') {
        // Destinos possíveis: formulários primeiro (é o que o cliente mais linka),
        // depois as seções. IDs faltantes são criados na hora — antes a lista vinha
        // vazia ("Nenhuma secao com ID encontrada") e não dava para linkar nada.
        const targets = collectScrollTargets(editor);
        const sectionOptions: { id: string; label: string }[] = [
          { id: '', label: 'Escolha para onde o botão leva...' },
          ...targets.map((tg) => ({ id: tg.id, label: tg.label })),
        ];
        addTraitIfMissing({
          type: 'select',
          name: 'data-scroll-to',
          label: 'Ir para',
          options: sectionOptions,
          changeProp: false,
        });
      }
      if (effectiveAction === 'whatsapp') {
        addTraitIfMissing({ type: 'text', name: 'data-whatsapp', label: 'WhatsApp (número)', placeholder: '5511999999999', changeProp: false });
        addTraitIfMissing({ type: 'text', name: 'data-whatsapp-msg', label: 'Mensagem WhatsApp', placeholder: 'Olá! Gostaria de saber mais...', changeProp: false });
      }
      if (effectiveAction === 'phone') {
        addTraitIfMissing({ type: 'text', name: 'data-phone', label: 'Telefone', placeholder: '5511999999999', changeProp: false });
      }
      if (effectiveAction === 'email') {
        addTraitIfMissing({ type: 'text', name: 'data-email', label: 'E-mail', placeholder: 'contato@empresa.com', changeProp: false });
        addTraitIfMissing({ type: 'text', name: 'data-email-subject', label: 'Assunto do E-mail', placeholder: 'Quero saber mais', changeProp: false });
      }

      // Add WhatsApp-specific traits if it's a WhatsApp component
      const compAttrs = component.getAttributes();
      const compType = compAttrs['data-component-type'] || '';
      if (compType === 'whatsapp-float' || compType === 'whatsapp-button') {
        if (!traits.where({ name: 'data-wpp-number' }).length) {
          traits.add([
            { type: 'text', name: 'data-wpp-number', label: 'Numero WhatsApp', placeholder: '5511999999999', changeProp: false },
            { type: 'text', name: 'data-wpp-message', label: 'Mensagem Padrao', placeholder: 'Ola! Gostaria de saber mais...', changeProp: false }
          ]);
        }
        if (compType === 'whatsapp-float' && !traits.where({ name: 'data-wpp-tooltip' }).length) {
          traits.add([
            { type: 'text', name: 'data-wpp-tooltip', label: 'Tooltip', placeholder: 'Fale conosco!', changeProp: false }
          ]);
        }
      }

      // Remover botão anterior se existir
      const oldBtn = document.getElementById('change-image-btn');
      if (oldBtn) oldBtn.remove();
      const oldFormBtn = document.getElementById('link-to-form-btn');
      if (oldFormBtn) oldFormBtn.remove();

      const tagName = component.get('tagName')?.toLowerCase();
      const type = component.get('type');

      // Alinhamento de verdade: "alinhei ao meio e não fica" acontece porque
      // text-align não move uma imagem/bloco com largura própria — quem move é
      // a margem automática. Estes botões aplicam a margem certa no elemento.
      const oldAlign = document.getElementById('align-block-btns');
      if (oldAlign) oldAlign.remove();
      const traitsPanelAlign = document.getElementById('gjs-traits');
      const alignTag = (component.get('tagName') || '').toLowerCase();
      const alinhavel = ['img', 'div', 'section', 'figure', 'form', 'video', 'iframe', 'table'].includes(alignTag);
      if (traitsPanelAlign && alinhavel) {
        const box = document.createElement('div');
        box.id = 'align-block-btns';
        box.style.cssText = 'padding:10px 0;margin:6px 0 2px;border-bottom:1px solid #374151;';
        box.innerHTML = '<div style="font-size:11px;color:#9ca3af;margin-bottom:6px;font-weight:600;">ALINHAR ESTE BLOCO</div>';
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;gap:6px;';
        ([
          ['⬅️ Esquerda', { 'margin-left': '0', 'margin-right': 'auto' }],
          ['⬛ Centro', { 'margin-left': 'auto', 'margin-right': 'auto' }],
          ['➡️ Direita', { 'margin-left': 'auto', 'margin-right': '0' }],
        ] as [string, Record<string, string>][]).forEach(([label, estilo]) => {
          const b2 = document.createElement('button');
          b2.textContent = label;
          b2.style.cssText = 'flex:1;padding:8px 4px;background:#374151;color:#e5e7eb;border:none;border-radius:6px;cursor:pointer;font-size:11px;font-weight:600;';
          b2.onclick = () => {
            // display:block é o que permite a margem automática funcionar em imagens
            component.addStyle({ display: 'block', ...estilo });
            toast.success('Alinhamento aplicado.');
          };
          row.appendChild(b2);
        });
        box.appendChild(row);
        traitsPanelAlign.insertBefore(box, traitsPanelAlign.firstChild);
      }

      // Atalho de 1 clique: "este botão leva ao formulário".
      // Era a dúvida do cliente ("na RD eu pegava o código do form e colava no
      // botão") — aqui vira um clique, sem precisar saber o que é âncora/ID.
      const cls = `${compAttrs.class || ''} ${(component.getClasses?.() || []).join(' ')}`.toLowerCase();
      const isClickable = tagName === 'button' || tagName === 'a'
        || /\bbtn\b|button|cta/.test(cls)
        || !!compAttrs['data-action'] || !!compAttrs['data-btn-action'];
      if (isClickable) {
        // Botão ainda "sem destino": abre direto em Configurações, que é onde
        // se escolhe para onde ele leva. Se já tem ação, mantém em Estilos
        // (aí o clique costuma ser para mudar cor/tamanho).
        const hasAction = !!compAttrs['data-action'] || !!compAttrs['data-btn-action']
          || (!!compAttrs.href && compAttrs.href !== '#');
        if (!hasAction) setRightPanel('traits');
        const traitsPanel = document.getElementById('gjs-traits');
        const formId = primaryFormId(editor);
        if (traitsPanel && formId) {
          const btn = document.createElement('button');
          btn.id = 'link-to-form-btn';
          btn.setAttribute('data-testid', 'link-to-form-btn');
          const alreadyLinked = compAttrs['data-action'] === 'scroll' && compAttrs['data-scroll-to'] === formId;
          btn.innerHTML = alreadyLinked ? '✅ Este botão já leva ao formulário' : '🔗 Fazer este botão levar ao formulário';
          btn.style.cssText = `width:100%;padding:12px;margin:10px 0;background:${alreadyLinked ? '#065f46' : '#059669'};color:white;border:none;border-radius:8px;cursor:pointer;font-weight:600;font-size:13px;`;
          btn.onclick = () => {
            component.addAttributes({ 'data-action': 'scroll', 'data-scroll-to': formId });
            if (tagName === 'a') component.addAttributes({ href: `#${formId}` });
            toast.success('Pronto! Ao clicar, a página rola até o formulário.');
            editor.select(null);
            setTimeout(() => editor.select(component), 60);
          };
          traitsPanel.insertBefore(btn, traitsPanel.firstChild);
        }
      }

      if (tagName === 'img' || type === 'image') {
        // Criar botão para trocar imagem
        const traitsPanel = document.getElementById('gjs-traits');
        if (traitsPanel) {
          const btn = document.createElement('button');
          btn.id = 'change-image-btn';
          btn.innerHTML = '📤 Trocar Imagem';
          btn.style.cssText = 'width:100%;padding:12px;margin:10px 0;background:#6366f1;color:white;border:none;border-radius:8px;cursor:pointer;font-weight:600;font-size:14px;';
          btn.onclick = () => {
            const currentSrc = component.getAttributes().src || '';
            setNewImageUrl(currentSrc);
            setSelectedImageComponent(component);
            imageModalSeqRef.current += 1;
            setShowImageModal(true);
          };
          traitsPanel.insertBefore(btn, traitsPanel.firstChild);
          setRightPanel('traits');
        }
      }
    });

    editor.on('component:deselected', () => {
      const oldBtn = document.getElementById('change-image-btn');
      if (oldBtn) oldBtn.remove();
      const oldFormBtn = document.getElementById('link-to-form-btn');
      if (oldFormBtn) oldFormBtn.remove();
      const oldAlignBtns = document.getElementById('align-block-btns');
      if (oldAlignBtns) oldAlignBtns.remove();
    });

    // When traits change, update derived attributes and refresh UI
    editor.on('component:update', (comp: any) => {
      const selected = editor.getSelected();
      if (selected && comp === selected) {
        const attrs = comp.getAttributes();

        // Update WhatsApp href when number/message changes (with guard to prevent loop)
        const wppType = attrs['data-component-type'] || '';
        if (wppType === 'whatsapp-float' || wppType === 'whatsapp-button') {
          const wppNum = attrs['data-wpp-number'] || '5511999999999';
          const wppMsg = attrs['data-wpp-message'] || '';
          const newHref = `https://wa.me/${wppNum.replace(/[^0-9]/g, '')}${wppMsg ? '?text=' + encodeURIComponent(wppMsg) : ''}`;
          if (attrs.href !== newHref && !(comp as any).__updatingWppHref) {
            (comp as any).__updatingWppHref = true;
            comp.addAttributes({ href: newHref });
            setTimeout(() => { (comp as any).__updatingWppHref = false; }, 100);
            return; // skip re-select on this cycle
          }
        }

        // When action type changes, re-select to refresh dynamic traits
        const lastAction = (comp as any).__lastAction;
        const currentAction = attrs['data-action'] || attrs['data-btn-action'];
        if (currentAction !== lastAction) {
          (comp as any).__lastAction = currentAction;
          editor.select(null);
          setTimeout(() => editor.select(comp), 50);
        }
      }
    });

    // Intercept GrapesJS asset manager — redirect to our custom image modal
    editor.on('run:open-assets', () => {
      editor.stopCommand('open-assets');
      const selected = editor.getSelected();
      if (selected) {
        const currentSrc = selected.getAttributes().src || '';
        setNewImageUrl(currentSrc);
        setSelectedImageComponent(selected);
        imageModalSeqRef.current += 1;
        setShowImageModal(true);
      } else {
        // Sem nada selecionado o clique não fazia absolutamente nada.
        toast('Selecione antes a imagem ou a seção que você quer alterar.', { icon: 'ℹ️' });
      }
    });

    // Set default device
    editor.on('load', () => {
      isEditorReadyRef.current = true;
      setIsEditorReady(true);

      // Force canvas to refresh its size
      setTimeout(() => {
        editor.refresh();

        // Inject link CSS and form prevention into canvas iframe
        try {
          const frame = (editor as any).Canvas?.getFrameEl?.() || document.querySelector('.gjs-frame') as HTMLIFrameElement;
          if (frame?.contentDocument) {
            const style = frame.contentDocument.createElement('style');
            style.textContent = `
              [data-href], [data-action] { cursor: pointer; transition: opacity 0.2s; }
              [data-href]:hover, [data-action]:hover { opacity: 0.85; }
              /* Support for text gradients */
              [style*="-webkit-background-clip: text"] {
                -webkit-text-fill-color: transparent;
                background-clip: text;
              }
              /* Image flexibility */
              img { max-width: 100%; height: auto; }
              img[style*="object-fit"] { width: 100%; }
            `;
            frame.contentDocument.head.appendChild(style);

            // Prevent form submissions inside the editor canvas
            const script = frame.contentDocument.createElement('script');
            script.textContent = `
              document.addEventListener('submit', function(e) { e.preventDefault(); e.stopPropagation(); return false; }, true);
              document.querySelectorAll('form').forEach(function(f) { f.removeAttribute('action'); f.removeAttribute('method'); });
              new MutationObserver(function() {
                document.querySelectorAll('form[action], form[method]').forEach(function(f) { f.removeAttribute('action'); f.removeAttribute('method'); });
              }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['action','method'] });
            `;
            frame.contentDocument.body.appendChild(script);
          }
        } catch { /* ignore */ }
      }, 100);

      // Add placeholders to all Style Manager inputs based on their label
      const addPlaceholders = () => {
        const smEl = document.getElementById('gjs-sm');
        if (!smEl) return;
        // Map label text to placeholder
        const placeholderMap: Record<string, string> = {
          'Fonte': 'Ex: Inter, Arial',
          'Tamanho': '16',
          'Peso': '400',
          'Espaçamento': '0',
          'Cor': '#000000',
          'Altura da linha': '1.5',
          'Sombra do Texto': '0 2px 4px #000',
          'Cor de Fundo': '#ffffff',
          'Gradiente / Imagem': 'linear-gradient(...)',
          'Largura da Borda': '1',
          'Cor da Borda': '#000000',
          'Sombra': '0 4px 6px rgba(0,0,0,0.1)',
          'Opacidade': '1',
          'Posição do Fundo': 'center',
          'Tamanho do Fundo': 'cover',
        };
        const properties = smEl.querySelectorAll('.gjs-sm-property');
        properties.forEach((prop: Element) => {
          const label = prop.querySelector('.gjs-sm-label');
          const input = prop.querySelector('.gjs-field input') as HTMLInputElement;
          if (label && input && !input.placeholder) {
            const labelText = label.textContent?.trim() || '';
            const ph = placeholderMap[labelText];
            if (ph) input.placeholder = ph;
            else if (!input.placeholder) input.placeholder = labelText;
          }
        });
      };
      // Run after sectors render and on component selection
      setTimeout(addPlaceholders, 1500);
      editor.on('component:selected', () => setTimeout(addPlaceholders, 200));

      // Reposition Spectrum color picker to the left of the trigger button
      const repositionColorPicker = () => {
        const observer = new MutationObserver(() => {
          const containers = document.querySelectorAll('.sp-container') as NodeListOf<HTMLElement>;
          containers.forEach(sp => {
            if (sp.style.display === 'none' || !sp.offsetParent) return;
            const rect = sp.getBoundingClientRect();
            const viewportW = window.innerWidth;
            // If the picker overflows the right edge, move it to the left
            if (rect.right > viewportW - 10) {
              const overflow = rect.right - viewportW + 20;
              const currentLeft = parseFloat(sp.style.left) || rect.left;
              sp.style.left = (currentLeft - overflow) + 'px';
            }
            // If the picker overflows the bottom, move it up
            const viewportH = window.innerHeight;
            if (rect.bottom > viewportH - 10) {
              const overflowY = rect.bottom - viewportH + 20;
              const currentTop = parseFloat(sp.style.top) || rect.top;
              sp.style.top = (currentTop - overflowY) + 'px';
            }
          });
        });
        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
      };
      repositionColorPicker();

      // Inject descriptions into style manager sectors
      setTimeout(() => {
        const sectorDescriptions: Record<string, string> = {
          'Texto': 'Fonte, tamanho, cor e alinhamento do texto',
          'Fundo e Gradiente': 'Cor de fundo, gradiente e imagem de fundo',
          'Imagem': 'Ajuste de tamanho, posicao e proporcao da imagem',
          'Tamanho': 'Largura, altura, padding e margem do elemento',
          'Borda': 'Arredondamento, estilo, sombra e formas especiais',
          'Aparência': 'Opacidade, overflow e cursor',
          'Layout': 'Posicionamento, flexbox e grid',
          'Transformação': 'Rotacao, escala e transicoes'
        };

        const sectorTitles = document.querySelectorAll('.gjs-sm-sector-title');
        sectorTitles.forEach((titleEl: Element) => {
          // Already injected? Skip
          if (titleEl.querySelector('.sector-desc')) return;
          // Get only the direct text content (first text node), not child elements
          let text = '';
          for (const node of Array.from(titleEl.childNodes)) {
            if (node.nodeType === Node.TEXT_NODE) {
              text += node.textContent || '';
            }
          }
          text = text.trim();
          const desc = sectorDescriptions[text];
          if (desc) {
            const descEl = document.createElement('span');
            descEl.className = 'sector-desc';
            descEl.style.cssText = 'display:block;font-size:10px;font-weight:400;color:#64748b;text-transform:none;letter-spacing:0;margin-top:2px;';
            descEl.textContent = desc;
            titleEl.appendChild(descEl);
          }
        });
      }, 1000);

      // Inject gradient editor UI into the style manager
      setTimeout(() => {
        const smEl = document.getElementById('gjs-sm');
        if (!smEl) return;

        // Create gradient editor container
        const gradientPanel = document.createElement('div');
        gradientPanel.id = 'gradient-editor-panel';
        gradientPanel.style.cssText = 'display:none;padding:12px;margin:8px;background:#1f2937;border-radius:10px;border:1px solid #374151;float:none;width:auto;clear:both;';
        // Build gradient panel using DOM to avoid any CSS inheritance issues
        const gp = gradientPanel;
        const ce = (tag: string, styles: string, parent: HTMLElement) => {
          const el = document.createElement(tag);
          el.setAttribute('style', styles);
          parent.appendChild(el);
          return el;
        };

        // Title
        const titleRow = ce('div', 'margin-bottom:12px;', gp);
        titleRow.innerHTML = '<span style="color:#e5e7eb;font-size:13px;font-weight:600;">Editor de Gradiente</span>';

        // Preview
        ce('div', 'height:40px;border-radius:8px;margin-bottom:12px;border:1px solid #374151;background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);', gp).id = 'gradient-preview';

        // Tipo
        const tipoRow = ce('div', 'display:flex;align-items:center;gap:8px;margin-bottom:10px;', gp);
        tipoRow.innerHTML = '<label style="color:#9ca3af;font-size:12px;white-space:nowrap;min-width:50px;">Tipo</label><select id="gradient-type" style="flex:1;background:#111827;color:#e5e7eb;border:1px solid #374151;border-radius:6px;padding:6px 8px;font-size:12px;"><option value="linear">Linear</option><option value="radial">Radial</option></select>';

        // Angulo
        const angleRow = ce('div', 'display:flex;align-items:center;gap:8px;margin-bottom:10px;', gp);
        angleRow.id = 'gradient-angle-row';
        angleRow.innerHTML = '<label style="color:#9ca3af;font-size:12px;white-space:nowrap;min-width:50px;">Ângulo</label><input type="range" id="gradient-angle" min="0" max="360" value="135" style="flex:1;accent-color:#6366f1;"><span id="gradient-angle-val" style="color:#e5e7eb;font-size:12px;min-width:32px;text-align:right;">135°</span>';

        // Cor Inicial
        const cor1Wrap = ce('div', 'margin-bottom:10px;', gp);
        cor1Wrap.innerHTML = '<label style="color:#9ca3af;font-size:11px;display:block;margin-bottom:4px;">Cor Inicial</label><div style="display:flex;gap:6px;align-items:center;"><input type="color" id="gradient-color1" value="#667eea" style="width:36px;height:32px;border:1px solid #374151;border-radius:6px;background:none;cursor:pointer;padding:2px;flex-shrink:0;"><input type="text" id="gradient-color1-hex" value="#667eea" style="flex:1;min-width:0;background:#111827;color:#e5e7eb;border:1px solid #374151;border-radius:6px;padding:6px 8px;font-size:12px;font-family:monospace;"></div>';

        // Cor Final
        const cor2Wrap = ce('div', 'margin-bottom:10px;', gp);
        cor2Wrap.innerHTML = '<label style="color:#9ca3af;font-size:11px;display:block;margin-bottom:4px;">Cor Final</label><div style="display:flex;gap:6px;align-items:center;"><input type="color" id="gradient-color2" value="#764ba2" style="width:36px;height:32px;border:1px solid #374151;border-radius:6px;background:none;cursor:pointer;padding:2px;flex-shrink:0;"><input type="text" id="gradient-color2-hex" value="#764ba2" style="flex:1;min-width:0;background:#111827;color:#e5e7eb;border:1px solid #374151;border-radius:6px;padding:6px 8px;font-size:12px;font-family:monospace;"></div>';

        // Posicoes
        const posRow = ce('div', 'display:flex;gap:8px;margin-bottom:12px;', gp);
        posRow.innerHTML = '<div style="flex:1;"><label style="color:#9ca3af;font-size:11px;display:block;margin-bottom:4px;">Posição Inicial (%)</label><input type="number" id="gradient-stop1" min="0" max="100" value="0" style="width:100%;background:#111827;color:#e5e7eb;border:1px solid #374151;border-radius:6px;padding:6px 8px;font-size:12px;box-sizing:border-box;"></div><div style="flex:1;"><label style="color:#9ca3af;font-size:11px;display:block;margin-bottom:4px;">Posição Final (%)</label><input type="number" id="gradient-stop2" min="0" max="100" value="100" style="width:100%;background:#111827;color:#e5e7eb;border:1px solid #374151;border-radius:6px;padding:6px 8px;font-size:12px;box-sizing:border-box;"></div>';

        // Aplicar em
        const targetRow = ce('div', 'display:flex;align-items:center;gap:8px;margin-bottom:12px;', gp);
        targetRow.innerHTML = '<label style="color:#9ca3af;font-size:12px;white-space:nowrap;min-width:50px;">Aplicar em</label><select id="gradient-target" style="flex:1;background:#111827;color:#e5e7eb;border:1px solid #374151;border-radius:6px;padding:6px 8px;font-size:12px;"><option value="background">Fundo do Elemento</option><option value="text">Texto (Gradiente no Texto)</option></select>';

        // Botoes
        const btnsRow = ce('div', 'display:flex;gap:8px;', gp);
        btnsRow.innerHTML = '<button id="gradient-apply" style="flex:1;padding:8px;background:#6366f1;color:white;border:none;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;">Aplicar Gradiente</button><button id="gradient-remove" style="padding:8px 12px;background:#374151;color:#e5e7eb;border:none;border-radius:6px;font-size:12px;cursor:pointer;">Remover</button>';

        // Insert at the top of the style manager
        smEl.insertBefore(gradientPanel, smEl.firstChild);

        // Gradient editor logic
        const updateGradientPreview = () => {
          const type = (document.getElementById('gradient-type') as HTMLSelectElement)?.value;
          const angle = (document.getElementById('gradient-angle') as HTMLInputElement)?.value;
          const c1 = (document.getElementById('gradient-color1') as HTMLInputElement)?.value;
          const c2 = (document.getElementById('gradient-color2') as HTMLInputElement)?.value;
          const s1 = (document.getElementById('gradient-stop1') as HTMLInputElement)?.value;
          const s2 = (document.getElementById('gradient-stop2') as HTMLInputElement)?.value;
          const preview = document.getElementById('gradient-preview');
          const angleRow = document.getElementById('gradient-angle-row');
          const angleVal = document.getElementById('gradient-angle-val');

          if (angleVal) angleVal.textContent = angle + '°';
          if (angleRow) angleRow.style.display = type === 'linear' ? 'flex' : 'none';

          let gradient: string;
          if (type === 'radial') {
            gradient = `radial-gradient(circle, ${c1} ${s1}%, ${c2} ${s2}%)`;
          } else {
            gradient = `linear-gradient(${angle}deg, ${c1} ${s1}%, ${c2} ${s2}%)`;
          }
          if (preview) preview.style.background = gradient;
          return gradient;
        };

        // Sync color picker <-> hex input
        const syncColorInputs = (colorId: string, hexId: string) => {
          const colorEl = document.getElementById(colorId) as HTMLInputElement;
          const hexEl = document.getElementById(hexId) as HTMLInputElement;
          if (!colorEl || !hexEl) return;
          colorEl.addEventListener('input', () => { hexEl.value = colorEl.value; updateGradientPreview(); });
          hexEl.addEventListener('input', () => {
            const v = hexEl.value;
            if (/^#[0-9a-fA-F]{6}$/.test(v)) { colorEl.value = v; updateGradientPreview(); }
          });
          hexEl.addEventListener('blur', () => {
            let v = hexEl.value.trim();
            if (!v.startsWith('#')) v = '#' + v;
            if (/^#[0-9a-fA-F]{6}$/.test(v)) { hexEl.value = v; colorEl.value = v; updateGradientPreview(); }
            else { hexEl.value = colorEl.value; }
          });
        };
        syncColorInputs('gradient-color1', 'gradient-color1-hex');
        syncColorInputs('gradient-color2', 'gradient-color2-hex');

        // Event listeners for all gradient controls
        ['gradient-type', 'gradient-angle', 'gradient-stop1', 'gradient-stop2'].forEach(id => {
          document.getElementById(id)?.addEventListener('input', updateGradientPreview);
        });

        // Apply gradient to selected component
        document.getElementById('gradient-apply')?.addEventListener('click', () => {
          const ed = editorRef.current;
          if (!ed) return;
          const selected = ed.getSelected();
          if (!selected) { return; }
          const gradient = updateGradientPreview();
          const target = (document.getElementById('gradient-target') as HTMLSelectElement)?.value;

          if (target === 'text') {
            // Apply gradient to text
            selected.addStyle({
              'background-image': gradient,
              '-webkit-background-clip': 'text',
              'background-clip': 'text',
              '-webkit-text-fill-color': 'transparent'
            });
          } else {
            // Apply gradient to background
            selected.addStyle({ 'background-image': gradient });
          }
        });

        // Remove gradient
        document.getElementById('gradient-remove')?.addEventListener('click', () => {
          const ed = editorRef.current;
          if (!ed) return;
          const selected = ed.getSelected();
          if (!selected) return;
          selected.addStyle({
            'background-image': 'none',
            '-webkit-background-clip': 'border-box',
            'background-clip': 'border-box',
            '-webkit-text-fill-color': 'inherit'
          });
          // Reset preview
          (document.getElementById('gradient-color1') as HTMLInputElement).value = '#667eea';
          (document.getElementById('gradient-color1-hex') as HTMLInputElement).value = '#667eea';
          (document.getElementById('gradient-color2') as HTMLInputElement).value = '#764ba2';
          (document.getElementById('gradient-color2-hex') as HTMLInputElement).value = '#764ba2';
          (document.getElementById('gradient-stop1') as HTMLInputElement).value = '0';
          (document.getElementById('gradient-stop2') as HTMLInputElement).value = '100';
          (document.getElementById('gradient-angle') as HTMLInputElement).value = '135';
          updateGradientPreview();
        });

        // Show/hide gradient panel and populate from selected component
        editor.on('component:selected', (comp: any) => {
          const panel = document.getElementById('gradient-editor-panel');
          if (!panel) return;
          panel.style.display = 'block';

          // Detect if text gradient is applied
          const bgClip = comp.getStyle()['-webkit-background-clip'] || comp.getStyle()['background-clip'] || '';
          const targetSelect = document.getElementById('gradient-target') as HTMLSelectElement;
          if (targetSelect) {
            targetSelect.value = bgClip === 'text' ? 'text' : 'background';
          }

          // Parse existing gradient from component style (handles #hex, rgb(), rgba())
          const bgImage = comp.getStyle()['background-image'] || comp.getStyle()['background'] || '';
          // Helper: extract hex color from match group (converts rgb to hex if needed)
          const colorToHex = (c: string): string => {
            if (!c) return '#667eea';
            c = c.trim();
            if (c.startsWith('#')) return c.length === 4 ? '#' + c[1]+c[1]+c[2]+c[2]+c[3]+c[3] : c;
            const rgbMatch = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (rgbMatch) {
              const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
              const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
              const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
              return '#' + r + g + b;
            }
            return c;
          };
          // Flexible regex: captures color (hex or rgb) and optional percentage
          const colorPattern = '(#[0-9a-fA-F]{3,8}|rgba?\\([^)]+\\))';
          const linearRe = new RegExp('linear-gradient\\((\\d+)deg,\\s*' + colorPattern + '\\s*(\\d+)?\\s*%?,\\s*' + colorPattern + '\\s*(\\d+)?\\s*%?\\)');
          const radialRe = new RegExp('radial-gradient\\(circle,\\s*' + colorPattern + '\\s*(\\d+)?\\s*%?,\\s*' + colorPattern + '\\s*(\\d+)?\\s*%?\\)');
          const linearMatch = bgImage.match(linearRe);
          const radialMatch = bgImage.match(radialRe);

          if (linearMatch) {
            const hex1 = colorToHex(linearMatch[2]);
            const hex2 = colorToHex(linearMatch[4]);
            (document.getElementById('gradient-type') as HTMLSelectElement).value = 'linear';
            (document.getElementById('gradient-angle') as HTMLInputElement).value = linearMatch[1];
            (document.getElementById('gradient-color1') as HTMLInputElement).value = hex1;
            (document.getElementById('gradient-color1-hex') as HTMLInputElement).value = hex1;
            (document.getElementById('gradient-color2') as HTMLInputElement).value = hex2;
            (document.getElementById('gradient-color2-hex') as HTMLInputElement).value = hex2;
            (document.getElementById('gradient-stop1') as HTMLInputElement).value = linearMatch[3] || '0';
            (document.getElementById('gradient-stop2') as HTMLInputElement).value = linearMatch[5] || '100';
          } else if (radialMatch) {
            const hex1 = colorToHex(radialMatch[1]);
            const hex2 = colorToHex(radialMatch[3]);
            (document.getElementById('gradient-type') as HTMLSelectElement).value = 'radial';
            (document.getElementById('gradient-color1') as HTMLInputElement).value = hex1;
            (document.getElementById('gradient-color1-hex') as HTMLInputElement).value = hex1;
            (document.getElementById('gradient-color2') as HTMLInputElement).value = hex2;
            (document.getElementById('gradient-color2-hex') as HTMLInputElement).value = hex2;
            (document.getElementById('gradient-stop1') as HTMLInputElement).value = radialMatch[2] || '0';
            (document.getElementById('gradient-stop2') as HTMLInputElement).value = radialMatch[4] || '100';
          }
          updateGradientPreview();
        });

        editor.on('component:deselected', () => {
          const panel = document.getElementById('gradient-editor-panel');
          if (panel) panel.style.display = 'none';
        });
      }, 500);

      // Load existing page data if editing
      if (isEditing && pageData) {
        try {
          const components = pageData.gjsComponents || pageData.gjs_components;
          const styles = pageData.gjsStyles || pageData.gjs_styles;
          if (components) {
            editor.setComponents(typeof components === 'string' ? JSON.parse(components) : components);
          }
          if (styles) {
            editor.setStyle(typeof styles === 'string' ? JSON.parse(styles) : styles);
          }
          // Texto colado do Word/Google Docs vem com classes+CSS do Office
          // (.TextRun, .SCXW…, .BCX…) que fixam cor/peso e VENCEM o painel de estilo —
          // a pessoa muda a cor e o texto continua preto/bold. Removemos esses
          // artefatos: o texto passa a herdar do container e o painel volta a mandar.
          setTimeout(() => sanitizeOfficeArtifacts(editor), 400);
        } catch (e) {
          console.error('Error loading page data:', e);
        }
      }

      // Click-to-add blocks (essential for mobile where drag doesn't work)
      setTimeout(() => {
        const blocksEl = document.getElementById('gjs-blocks');
        if (blocksEl) {
          let isDragging = false;
          blocksEl.addEventListener('mousedown', () => { isDragging = false; });
          blocksEl.addEventListener('mousemove', () => { isDragging = true; });
          blocksEl.addEventListener('touchstart', () => { isDragging = false; }, { passive: true });
          blocksEl.addEventListener('touchmove', () => { isDragging = true; }, { passive: true });

          blocksEl.addEventListener('click', (e: Event) => {
            if (isDragging) return;

            // Use editorRef to get latest editor instance
            const ed = editorRef.current;
            if (!ed) return;

            const blockEl = (e.target as HTMLElement).closest('.gjs-block') as HTMLElement;
            if (!blockEl) return;

            // Get label text from the block element
            const labelEl = blockEl.querySelector('.gjs-block-label');
            if (!labelEl) return;
            const clickedLabel = (labelEl.textContent || '').trim();
            if (!clickedLabel) return;

            // Find matching block in BlockManager by label
            const bm = (ed as any).Blocks || (ed as any).BlockManager;
            if (!bm) return;
            const allBlocks = bm.getAll();
            let matchedBlock: any = null;

            allBlocks.forEach((block: any) => {
              const blockLabel = (block.get('label') || '')
                .replace(/<[^>]*>/g, '')
                .trim();
              if (blockLabel === clickedLabel && !matchedBlock) {
                matchedBlock = block;
              }
            });

            if (matchedBlock) {
              const content = matchedBlock.get('content');
              if (!content) return;

              try {
                // Use DomComponents to add content safely
                const wrapper = ed.getWrapper();
                if (typeof content === 'string') {
                  wrapper.append(content);
                } else {
                  // For object/component content, deep clone to avoid reference issues
                  wrapper.append(JSON.parse(JSON.stringify(content)));
                }
              } catch (addErr) {
                console.warn('Could not add block:', addErr);
                return;
              }

              // Close sidebar on mobile only
              if (window.innerWidth < 1024) {
                setShowLeftSidebar(false);
              }

              // Scroll canvas to bottom
              setTimeout(() => {
                try {
                  const frame = (ed as any).Canvas?.getFrameEl?.() || document.querySelector('.gjs-frame') as HTMLIFrameElement;
                  if (frame?.contentWindow) {
                    frame.contentWindow.scrollTo({
                      top: frame.contentWindow.document.body.scrollHeight,
                      behavior: 'smooth'
                    });
                  }
                } catch { /* ignore scroll errors */ }
              }, 300);
            }
          });
        }
      }, 500);
    });

    // Ao trocar a COR pelo painel, cascateia p/ os filhos que têm cor própria. Spans de
    // colagem do Word carregam color:rgb(0,0,0) numa regra por id que vencia a cor do pai
    // — pintar o parágrafo de branco não mudava o texto interno. Aqui, ao pintar um
    // elemento, sobrescrevemos a cor de qualquer descendente que tenha cor explícita.
    let __cascadingColor = false;
    const cascadeColorToChildren = (root: any, color: string) => {
      (root.components?.() || []).forEach((child: any) => {
        // INCONDICIONAL: a cor de um filho pode viver numa regra por id (lixo do Word),
        // que NÃO aparece em getStyle() — filtrar por "tem cor" pulava justamente esses.
        // addStyle inline vence a regra por id. Só elementos (tagName), não text nodes.
        if (child.get?.('tagName')) {
          child.addStyle({ color, '-webkit-text-fill-color': color });
        }
        cascadeColorToChildren(child, color);
      });
    };
    editor.on('component:styleUpdate:color', () => {
      if (__cascadingColor) return;
      const target = editor.getSelected();
      const color = target?.getStyle?.()?.['color'];
      if (!target || !color) return;
      __cascadingColor = true;
      try { cascadeColorToChildren(target, color); }
      catch (e) { /* noop */ }
      finally { __cascadingColor = false; }
    });

    editorRef.current = editor;

    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load page data when editing
  useEffect(() => {
    const loadPage = async () => {
      if (isEditing && id) {
        try {
          setIsLoading(true);
          const response = await landingPageService.getLandingPage(id);
          if (response.success && response.data) {
            const page = response.data;
            setPageName(page.name);
            setPageSlug(page.slug);
            setPageData(page);
            setPageId(page.id);

            // Load custom code fields
            setCustomCode({
              headCode: page.head_code || page.headCode || '',
              bodyStartCode: page.body_start_code || page.bodyStartCode || '',
              bodyEndCode: page.body_end_code || page.bodyEndCode || '',
              customCss: page.custom_css || page.customCss || ''
            });

            // Load thumbnail if available
            if (page.thumbnail || page.og_image) {
              setPageThumbnail(page.thumbnail || page.og_image || null);
            }

            // Detect imported HTML pages (have raw gjs_html but no GrapesJS components).
            // These bypass GrapesJS entirely — see isImportedHtml above.
            const components = page.gjsComponents || page.gjs_components;
            const hasComponents = !!components && components !== '' && components !== '[]';
            const imported = !!page.gjs_html && !hasComponents;
            if (imported) {
              // Página HTML: editada VISUALMENTE no GrapesJS e salva como documento
              // HTML completo. O corpo entra no canvas; head/scripts são preservados.
              setIsImportedHtml(true);
              setImportedHtml(page.gjs_html || '');
              setImportedCss(page.gjs_css || '');
              const doc = isFullHtmlDocument(page.gjs_html || '')
                ? (page.gjs_html || '')
                : reconstructFullHtml({
                    html: page.gjs_html || '',
                    css: page.gjs_css || '',
                    headCode: page.head_code || page.headCode || '',
                    bodyEndCode: page.body_end_code || page.bodyEndCode || '',
                    title: page.name || ''
                  });
              setFullHtml(doc);
              // Carrega no editor visual (se pronto; senão fica pendente p/ o effect).
              if (editorRef.current && isEditorReadyRef.current) {
                loadHtmlIntoEditor(doc);
              } else {
                pendingHtmlLoadRef.current = doc;
              }
            } else if (editorRef.current) {
              // Component-based page: load into GrapesJS
              try {
                if (page.gjsComponents) {
                  editorRef.current.setComponents(JSON.parse(page.gjsComponents));
                } else if (page.gjs_components) {
                  editorRef.current.setComponents(JSON.parse(page.gjs_components));
                }
                if (page.gjsStyles) {
                  editorRef.current.setStyle(JSON.parse(page.gjsStyles));
                } else if (page.gjs_styles) {
                  editorRef.current.setStyle(JSON.parse(page.gjs_styles));
                }
              } catch (e) {
                console.error('Error loading page content:', e);
              }
            }
          }
        } catch (error) {
          console.error('Error loading page:', error);
          toast.error(t('pageBuilder.messages.loadError'));
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadPage();
  }, [isEditing, id, t]);

  // Update device in editor
  useEffect(() => {
    if (editorRef.current && isEditorReady) {
      editorRef.current.setDevice(activeDevice);
    }
  }, [activeDevice, isEditorReady]);

  // Refresh GrapesJS canvas when sidebars toggle on desktop (layout changes)
  useEffect(() => {
    if (isDesktop && editorRef.current && isEditorReady) {
      // Small delay to let CSS transition finish
      const timer = setTimeout(() => {
        editorRef.current?.refresh();
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [showLeftSidebar, showRightSidebar, isDesktop, isEditorReady]);

  // Handle slug generation from name
  useEffect(() => {
    if (!isEditing) {
      const slug = pageName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setPageSlug(slug || 'nova-landing-page');
    }
  }, [pageName, isEditing]);

  // Auto-open publish modal when returning from Vercel OAuth
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('vercel') === 'connected') {
      // Clean URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('vercel');
      window.history.replaceState({}, '', newUrl.toString());
      // Open publish modal
      setShowPublishModal(true);
      toast.success('Vercel connected successfully!');
    }
  }, []);

  // ===== Páginas HTML editadas no GrapesJS (WYSIWYG, salvas como HTML) =====
  // Reconstrói o documento HTML ATUAL a partir do editor visual (fonte da verdade)
  // + head/scripts preservados. Usado no Salvar/Publicar e ao abrir IA/HTML.
  const currentFullHtml = useCallback((): string => {
    const editor = editorRef.current;
    if (!editor) return fullHtml;
    return buildFullDoc({
      head: importedHeadRef.current,
      body: editor.getHtml(),
      css: editor.getCss() || '',
      scripts: importedScriptsRef.current,
      title: pageName,
    });
  }, [pageName, fullHtml]);

  // Injeta os estilos/fontes do <head> da PÁGINA dentro do iframe do canvas do
  // GrapesJS. Sem isto, páginas com CSS próprio (classes .hero/.btn, variáveis
  // CSS, Google Fonts) renderizavam sem estilo no editor ("quebrava tudo") porque
  // o canvas só tinha Tailwind+Inter genéricos.
  const injectPageHeadIntoCanvas = useCallback((headHtml: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const apply = () => {
      try {
        const canvasDoc: Document | undefined = (editor as any).Canvas?.getDocument?.();
        if (!canvasDoc || !canvasDoc.head) return false;
        // remove injeções anteriores (recarga/edição por IA)
        canvasDoc.querySelectorAll('[data-lp-head="1"]').forEach((e) => e.remove());
        const tmp = canvasDoc.createElement('div');
        tmp.innerHTML = headHtml || '';
        Array.from(tmp.childNodes).forEach((node) => {
          const tag = (node as any).tagName?.toLowerCase?.();
          if (tag !== 'style' && tag !== 'link') return; // só estilos/fontes/CSS
          const src = node as HTMLElement;
          const el = canvasDoc.createElement(tag);
          Array.from(src.attributes || []).forEach((a) => el.setAttribute(a.name, a.value));
          if (tag === 'style') el.textContent = src.textContent || '';
          el.setAttribute('data-lp-head', '1');
          canvasDoc.head.appendChild(el);
        });
        // Animações de entrada (elemento nasce opacity:0 e o JS da página revela ao
        // rolar) deixavam o texto INVISÍVEL no editor — o JS não roda no canvas.
        // Neutraliza as classes comuns só dentro do editor; o publish não muda.
        const fixAnim = canvasDoc.createElement('style');
        fixAnim.setAttribute('data-lp-head', '1');
        fixAnim.textContent = '.reveal,[data-aos],.aos-init,.fade-in,.fade-up,.animate-on-scroll,.wow{opacity:1!important;transform:none!important;visibility:visible!important}';
        canvasDoc.head.appendChild(fixAnim);
        return true;
      } catch (e) { console.error('injectPageHeadIntoCanvas error:', e); return false; }
    };
    // aplica agora e re-tenta (o iframe do canvas pode não estar 100% pronto).
    if (!apply()) setTimeout(apply, 150);
    setTimeout(apply, 400);
  }, []);

  // Carrega um documento HTML no editor visual: separa head/scripts (preservados),
  // joga o corpo no canvas do GrapesJS e injeta o CSS do head para render fiel.
  const loadHtmlIntoEditor = useCallback((doc: string) => {
    const { head, body, scripts } = splitHtmlDoc(doc);
    importedHeadRef.current = head;
    importedScriptsRef.current = scripts;
    const editor = editorRef.current;
    if (editor) {
      try {
        editor.DomComponents.clear();
        editor.setComponents(body);
        injectPageHeadIntoCanvas(head);
      } catch (e) { console.error('loadHtmlIntoEditor error:', e); }
    }
  }, [injectPageHeadIntoCanvas]);

  // Carrega o HTML pendente quando o editor fica pronto (evita perder o
  // setComponents se a página abriu antes do GrapesJS terminar de montar).
  const pendingHtmlLoadRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditorReady && pendingHtmlLoadRef.current != null) {
      loadHtmlIntoEditor(pendingHtmlLoadRef.current);
      pendingHtmlLoadRef.current = null;
    }
    // isLoading entra nas dependências de propósito: quando a API responde
    // depois do editor ficar pronto, é essa mudança que dispara o carregamento.
  }, [isEditorReady, isLoading, loadHtmlIntoEditor]);

  // ===== Proteções contra salvar/publicar uma página vazia =====
  // Salvar enquanto a página ainda estava carregando gravava o editor VAZIO por
  // cima do conteúdo do cliente (perda total). Duas travas: (1) espera o
  // conteúdo entrar no editor; (2) se mesmo assim o editor estiver vazio e a
  // versão salva tiver conteúdo, pede confirmação antes de sobrescrever.
  const isPageLoading = isEditing && isLoading;

  const hasVisibleContent = useCallback((html: string): boolean => {
    if (!html || !html.trim()) return false;
    try {
      const doc = new DOMParser().parseFromString(`<div id="dsk-check">${html}</div>`, 'text/html');
      const root = doc.getElementById('dsk-check');
      if (!root) return true; // não consegui avaliar → não atrapalha o save
      if ((root.textContent || '').trim().length > 0) return true;
      return root.querySelectorAll('img, iframe, video, svg, input, textarea, select, hr, picture, canvas').length > 0;
    } catch {
      return true;
    }
  }, []);

  // true → pode salvar. false → o usuário desistiu de sobrescrever.
  const confirmIfEmptyOverwrite = useCallback((action: 'salvar' | 'publicar'): boolean => {
    const editor = editorRef.current;
    if (!editor) return true;
    const now = (() => { try { return editor.getHtml() || ''; } catch { return ''; } })();
    if (hasVisibleContent(now)) return true;
    const savedHadContent = hasVisibleContent(pageData?.gjs_html || '')
      || !!(pageData?.gjs_components && pageData.gjs_components !== '[]')
      || hasVisibleContent(fullHtml || importedHtml || '');
    if (!savedHadContent) return true;
    return window.confirm(
      `A página está VAZIA no editor, mas a versão salva tem conteúdo.\n\n` +
      `Se ${action} agora, o conteúdo atual será apagado.\n\n` +
      `Se você não apagou nada de propósito, cancele e recarregue a página. Continuar mesmo assim?`
    );
  }, [hasVisibleContent, pageData, fullHtml, importedHtml]);

  // Nome vazio ou URL malformada faziam o backend recusar o save com uma
  // mensagem técnica ("Slug invalido") e o cliente perdia o trabalho. Aqui
  // normalizamos o que dá para consertar e avisamos em português o resto.
  const normalizeSlug = useCallback((raw: string): string => (raw || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, ''), []);

  // Retorna null quando não dá para salvar (o motivo já foi avisado ao usuário).
  const sanitizedIdentity = useCallback((): { name: string; slug: string } | null => {
    const name = (pageName || '').trim();
    if (!name) {
      toast.error('Dê um nome para a página antes de salvar (campo no topo, ao lado da seta de voltar).');
      return null;
    }
    const slug = normalizeSlug(pageSlug) || normalizeSlug(name) || 'landing-page';
    if (slug !== pageSlug) setPageSlug(slug);
    return { name, slug };
  }, [pageName, pageSlug, normalizeSlug]);

  // ===== Ajuste automático de responsividade =====
  // O cliente não consegue "desencavalar" seções no celular na mão (o HTML
  // importado usa posição absoluta e larguras fixas). Este comando mede a
  // página na largura do aparelho e grava as correções DENTRO da media query
  // daquele aparelho — o desktop, que já está certo, não é tocado.
  const handleAutoResponsive = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return;
    let device: 'Tablet' | 'Mobile' = activeDevice === 'Tablet' ? 'Tablet' : 'Mobile';
    if (activeDevice === 'Desktop') {
      setActiveDevice('Mobile');
      editor.setDevice('Mobile');
      device = 'Mobile';
    }
    setIsFixingResponsive(true);
    // Limpa um ajuste de sessão anterior guardado no <head> da página HTML —
    // senão as regras antigas se acumulariam e atrapalhariam a nova medição.
    if (importedHeadRef.current.includes('--dsk-mobile-fix')) {
      const cleaned = stripFixFromHtmlHead(importedHeadRef.current);
      importedHeadRef.current = cleaned.html;
      injectPageHeadIntoCanvas(cleaned.html);
    }
    // Espera o canvas refluir na largura do aparelho antes de medir.
    await new Promise((res) => setTimeout(res, 500));
    try {
      // Se o cliente trocou de aparelho no meio do ajuste, mediríamos numa
      // largura e gravaríamos o CSS na media query de outra. Reafirma o
      // aparelho e deixa o canvas assentar antes de medir.
      try {
        if ((editor as any).getDevice?.() !== device) {
          editor.setDevice(device);
          setActiveDevice(device);
          await new Promise((res) => setTimeout(res, 400));
        }
      } catch { /* getDevice indisponível: segue com o device pedido */ }
      const result = autoFixResponsive(editor, device);
      setResponsiveFixApplied(hasResponsiveFix(editor));
      const tela = device === 'Mobile' ? 'celular' : 'tablet';
      if (result.fixed === 0) {
        toast.success(`Tudo certo no ${tela}: nenhuma seção encavalada encontrada.`);
      } else {
        toast.success(
          `${result.fixed} ${result.fixed === 1 ? 'ajuste aplicado' : 'ajustes aplicados'} no ${tela}. O desktop continua igual.`,
          { duration: 6000 }
        );
      }
    } catch (e) {
      console.error('[responsive] falhou:', e);
      toast.error('Não consegui organizar esta tela. Tente novamente.');
    } finally {
      setIsFixingResponsive(false);
    }
  }, [activeDevice, injectPageHeadIntoCanvas]);

  // ===== Formulário externo (RD Station e afins) =====
  // HTML exportado do RD traz só a "casca" do formulário: os campos são criados
  // por um script do RD que não roda fora do domínio deles. Publicado, vira uma
  // caixa sem campos — o lead nunca chega. Aqui trocamos pela versão DevSkin,
  // que já cai no CRM/fluxo.
  const handleInsertDevskinForm = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    try {
      const issues = detectExternalForms(editor, `${importedHeadRef.current} ${importedScriptsRef.current}`);
      const target = issues.find((i) => i.component)?.component;
      // trim: sem isso o parser cria um nó de texto vazio antes do <form> e a
      // seleção/rolagem cairia nele em vez de no formulário.
      const markup = LEAD_FORM_CARD_HTML.trim();
      const pickForm = (added: any): any => {
        const list = Array.isArray(added) ? added : [added];
        return list.find((c: any) => String(c?.get?.('tagName') || '').toLowerCase() === 'form') || list[0] || null;
      };
      let created: any = null;
      if (target && target.parent?.()) {
        const parent = target.parent();
        const at = typeof target.index === 'function' ? target.index() : undefined;
        created = pickForm(parent.append(markup, at != null ? { at } : {}));
        target.remove();
      } else {
        created = pickForm(editor.getWrapper()?.append(markup));
      }
      if (created) {
        editor.select(created);
        setTimeout(() => { try { created.getEl?.()?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* ignore */ } }, 150);
      }
      setExternalFormWarning(null);
      setExternalFormDismissed(true);
      toast.success('Formulário DevSkin inserido. Os leads caem direto no seu CRM/fluxo.', { duration: 6000 });
    } catch (e) {
      console.error('[form] falha ao inserir formulário DevSkin:', e);
      toast.error('Não consegui inserir o formulário. Arraste o bloco "Formulário (card)" pelo painel de Blocos.');
    }
  }, []);

  // Detecta o formulário externo assim que a página termina de carregar.
  useEffect(() => {
    if (!isEditorReady || isLoading || externalFormDismissed) return;
    const timer = setTimeout(() => {
      const editor = editorRef.current;
      if (!editor) return;
      const issues = detectExternalForms(editor, `${importedHeadRef.current} ${importedScriptsRef.current}`);
      if (issues.length) setExternalFormWarning({ provider: issues[0].provider, reason: issues[0].reason });
    }, 1500);
    return () => clearTimeout(timer);
  }, [isEditorReady, isLoading, externalFormDismissed]);

  const handleUndoResponsive = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    let removed = clearResponsiveFix(editor);
    // Páginas HTML guardam o CSS no <head> preservado: ajustes de sessões
    // anteriores só saem limpando esse texto também.
    const head = importedHeadRef.current;
    if (head && head.includes('--dsk-mobile-fix')) {
      const cleaned = stripFixFromHtmlHead(head);
      importedHeadRef.current = cleaned.html;
      removed += cleaned.removed;
      injectPageHeadIntoCanvas(cleaned.html);
    }
    setResponsiveFixApplied(hasResponsiveFix(editor) || importedHeadRef.current.includes('--dsk-mobile-fix'));
    toast.success(removed ? 'Organização automática desfeita.' : 'Não havia ajuste automático aplicado.');
  }, [injectPageHeadIntoCanvas]);

  // Reconhece um ajuste feito numa sessão anterior (a marca vai junto no CSS
  // salvo), para o botão "Desfazer" aparecer também ao reabrir a página.
  useEffect(() => {
    if (!isEditorReady || !editorRef.current) return;
    const timer = setTimeout(() => {
      if (!editorRef.current) return;
      setResponsiveFixApplied(
        hasResponsiveFix(editorRef.current) || importedHeadRef.current.includes('--dsk-mobile-fix')
      );
    }, 1200);
    return () => clearTimeout(timer);
  }, [isEditorReady, isLoading]);

  // Capture canvas screenshot
  const captureCanvasScreenshot = useCallback(async (): Promise<string | null> => {
    if (!editorRef.current) {
      console.log('[Screenshot] Editor not ready');
      return null;
    }

    try {
      console.log('[Screenshot] Starting capture...');

      const editor = editorRef.current;
      const html = editor.getHtml();
      const css = editor.getCss() || '';

      // Get wrapper/body styles from gjs_styles
      let wrapperStyles = '';
      try {
        const gjsStyles = editor.getStyle();
        if (gjsStyles && Array.isArray(gjsStyles)) {
          const wrapperRule = gjsStyles.find((rule: any) =>
            rule.selectors?.includes('wrapper') ||
            rule.selectorsAdd?.includes('[data-gjs-type="wrapper"]')
          );
          if (wrapperRule?.style) {
            wrapperStyles = Object.entries(wrapperRule.style)
              .map(([k, v]) => `${k}: ${v}`)
              .join('; ');
          }
        }
      } catch (e) {
        console.warn('[Screenshot] Could not extract wrapper styles:', e);
      }

      // Create a hidden iframe for rendering with full Tailwind support
      const iframe = document.createElement('iframe');
      iframe.style.cssText = `
        position: fixed;
        left: -9999px;
        top: 0;
        width: 1280px;
        height: 800px;
        border: none;
        z-index: -1;
      `;
      document.body.appendChild(iframe);

      // Build full HTML document with Tailwind
      const fullHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <script src="https://cdn.tailwindcss.com"></script>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Inter', sans-serif; background: #ffffff; }
            ${css}
          </style>
        </head>
        <body style="${wrapperStyles}">
          ${html}
        </body>
        </html>
      `;

      // Write content to iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        document.body.removeChild(iframe);
        console.warn('[Screenshot] Could not access iframe document');
        return null;
      }

      iframeDoc.open();
      iframeDoc.write(fullHtml);
      iframeDoc.close();

      console.log('[Screenshot] Iframe created, waiting for Tailwind to process...');

      // Wait for Tailwind CSS to process and images to load
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Capture the iframe body
      const canvas = await html2canvas(iframeDoc.body, {
        width: 1280,
        height: 800,
        scale: 0.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        foreignObjectRendering: false
      });

      // Clean up iframe
      document.body.removeChild(iframe);

      console.log('[Screenshot] Canvas captured, size:', canvas.width, 'x', canvas.height);

      // Convert to blob and upload
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.8);
      });

      if (!blob) {
        console.warn('[Screenshot] Failed to create blob');
        return null;
      }

      console.log('[Screenshot] Blob created, size:', blob.size, 'bytes. Uploading...');

      // Upload to S3 via API
      const formData = new FormData();
      formData.append('file', blob, `thumbnail-${Date.now()}.jpg`);

      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('[Screenshot] Upload response:', response.data);

      if (response.data?.success && (response.data?.url || response.data?.data?.url)) {
        const url = response.data.url || response.data.data.url;
        console.log('[Screenshot] Success! URL:', url);
        setPageThumbnail(url);
        return url;
      }

      console.warn('[Screenshot] Upload failed:', response.data);
      return null;
    } catch (error) {
      console.error('[Screenshot] Error:', error);
      return null;
    }
  }, []);

  // Save handler
  // Ref mirror of isSaving so the closure inside useCallback always sees the
  // latest value (isSaving isn't in the deps, so the state check alone would
  // be stale after the first save).
  const isSavingRef = useRef(false);
  const handleSave = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!isImportedHtml && !editorRef.current) return;
    // Ainda buscando a página no servidor: salvar agora gravaria o editor vazio.
    if (isPageLoading) {
      toast.error('Espere a página terminar de carregar antes de salvar.');
      return;
    }
    if (!confirmIfEmptyOverwrite('salvar')) return;
    const identity = sanitizedIdentity();
    if (!identity) return;

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      // Capture screenshot before saving (component-based pages only —
      // imported HTML lives in an iframe, so we keep its existing thumbnail).
      let thumbnail = pageThumbnail;
      if (!isImportedHtml) {
        try {
          const newThumbnail = await captureCanvasScreenshot();
          if (newThumbnail) {
            thumbnail = newThumbnail;
          }
        } catch (e) {
          console.warn('Could not capture thumbnail:', e);
        }
      }

      const saveData: any = isImportedHtml
        ? {
            name: identity.name,
            slug: identity.slug,
            // Página importada é salva como DOCUMENTO HTML COMPLETO em gjs_html.
            // Os campos de "partes" ficam vazios para o deploy não injetar head/
            // body duas vezes — todo o head/body do usuário já vive em fullHtml.
            gjs_html: currentFullHtml(),
            gjs_css: '',
            gjs_components: '',
            gjs_styles: '',
            head_code: '',
            body_start_code: '',
            body_end_code: '',
            custom_css: '',
            flux_enabled: pageData?.flux_enabled || false,
            flux_api_key: pageData?.flux_api_key || '',
            flux_app_id: pageData?.flux_app_id || '',
            linked_pixels: pageData?.linked_pixels || []
          }
        : {
            name: identity.name,
            slug: identity.slug,
            gjs_html: editorRef.current!.getHtml(),
            gjs_css: editorRef.current!.getCss() || '',
            gjs_components: JSON.stringify(editorRef.current!.getComponents()),
            gjs_styles: JSON.stringify(editorRef.current!.getStyle()),
            // Custom code fields
            head_code: customCode.headCode,
            body_start_code: customCode.bodyStartCode,
            body_end_code: customCode.bodyEndCode,
            custom_css: customCode.customCss,
            // Flux Analytics - preserve existing settings
            flux_enabled: pageData?.flux_enabled || false,
            flux_api_key: pageData?.flux_api_key || '',
            flux_app_id: pageData?.flux_app_id || '',
            // Linked Pixels - preserve existing settings
            linked_pixels: pageData?.linked_pixels || []
          };

      // Include thumbnail if available
      if (thumbnail) {
        saveData.thumbnail = thumbnail;
      }

      if (pageId) {
        // Update existing page
        const response = await landingPageService.updateLandingPage(pageId, saveData);
        if (response.success) {
          toast.success(t('pageBuilder.messages.saved'));
        } else {
          throw new Error(response.message || 'Erro ao salvar');
        }
      } else {
        // Create new page
        const response = await landingPageService.createLandingPage(saveData);
        if (response.success && response.data) {
          setPageId(response.data.id);
          // Update URL without reloading
          window.history.replaceState(null, '', `/landing-pages/${response.data.id}/edit`);
          toast.success(t('pageBuilder.messages.saved'));
        } else {
          throw new Error(response.message || 'Erro ao criar página');
        }
      }
    } catch (error: any) {
      console.error('Save error:', error);
      toast.error(error.message || t('pageBuilder.messages.saveError'));
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [pageName, pageSlug, pageId, pageData, customCode, pageThumbnail, captureCanvasScreenshot, t, isImportedHtml, importedHtml, importedCss, fullHtml, currentFullHtml, isPageLoading, confirmIfEmptyOverwrite, sanitizedIdentity]);

  // Publish handler
  const handlePublish = useCallback(async (): Promise<{ url: string; success: boolean; error?: string }> => {
    if (!isImportedHtml && !editorRef.current) {
      return { url: '', success: false, error: 'Editor não inicializado' };
    }
    if (isPublishing) {
      return { url: '', success: false, error: 'Publicação em andamento' };
    }
    if (isPageLoading) {
      return { url: '', success: false, error: 'A página ainda está carregando. Espere terminar e publique de novo.' };
    }
    if (!confirmIfEmptyOverwrite('publicar')) {
      return { url: '', success: false, error: 'Publicação cancelada (a página está vazia no editor).' };
    }
    const pubIdentity = sanitizedIdentity();
    if (!pubIdentity) {
      return { url: '', success: false, error: 'Dê um nome para a página antes de publicar.' };
    }

    // Última barreira antes de a campanha ir ao ar: se a página só tem um
    // formulário externo (RD Station e afins) ou um <form> sem campos, o
    // visitante não consegue se inscrever e o lead se perde silenciosamente.
    if (editorRef.current) {
      const issues = detectExternalForms(editorRef.current, `${importedHeadRef.current} ${importedScriptsRef.current}`);
      if (issues.length) {
        const aviso = issues[0].reason === 'empty-form'
          ? 'Atenção: existe um formulário SEM NENHUM CAMPO nesta página — publicado, ele vira uma caixa vazia e ninguém consegue se inscrever.'
          : `Atenção: o formulário do ${issues[0].provider} não funciona na página publicada — ele chega sem os campos e você perde os leads.`;
        const ok = window.confirm(
          `${aviso}\n\n` +
          'Recomendado: cancelar, clicar em "Usar formulário da DevSkin" no aviso do editor e publicar depois.\n\n' +
          'Publicar assim mesmo?'
        );
        if (!ok) {
          return {
            url: '',
            success: false,
            error: 'Publicação cancelada. Clique em "Usar formulário da DevSkin" no aviso amarelo do editor e publique de novo.',
          };
        }
      }

      // Botão apontando para uma seção/formulário que não existe mais: o clique
      // simplesmente não faz nada e isso só apareceria com a campanha no ar.
      const broken = detectBrokenAnchors(editorRef.current);
      if (broken.length) {
        const lista = broken.slice(0, 4).map((b) => `• ${b.label} → #${b.target}`).join('\n');
        const ok = window.confirm(
          `Atenção: ${broken.length === 1 ? 'um botão aponta' : `${broken.length} botões apontam`} para uma parte da página que não existe mais:\n\n` +
          `${lista}\n\nClicar neles não vai levar a lugar nenhum. Publicar assim mesmo?`
        );
        if (!ok) {
          return { url: '', success: false, error: 'Publicação cancelada. Ajuste o destino dos botões e publique de novo.' };
        }
      }
    }

    setIsPublishing(true);

    try {
      const saveData = isImportedHtml
        ? {
            name: pubIdentity.name,
            slug: pubIdentity.slug,
            // MESMO payload do handleSave: publica o HTML EDITADO (fullHtml), não o
            // importedHtml original. Sem isto, Publicar descartava as edições da IA
            // e regredia o registro salvo. Campos de "partes" ficam vazios.
            gjs_html: currentFullHtml(),
            gjs_css: '',
            gjs_components: '',
            gjs_styles: '',
            head_code: '',
            body_start_code: '',
            body_end_code: '',
            custom_css: '',
            flux_enabled: pageData?.flux_enabled || false,
            flux_api_key: pageData?.flux_api_key || '',
            flux_app_id: pageData?.flux_app_id || '',
            linked_pixels: pageData?.linked_pixels || []
          }
        : {
            name: pubIdentity.name,
            slug: pubIdentity.slug,
            gjs_html: editorRef.current!.getHtml(),
            gjs_css: editorRef.current!.getCss() || '',
            gjs_components: JSON.stringify(editorRef.current!.getComponents()),
            gjs_styles: JSON.stringify(editorRef.current!.getStyle()),
            head_code: customCode.headCode,
            body_start_code: customCode.bodyStartCode,
            body_end_code: customCode.bodyEndCode,
            custom_css: customCode.customCss,
            // Flux Analytics - preserve existing settings
            flux_enabled: pageData?.flux_enabled || false,
            flux_api_key: pageData?.flux_api_key || '',
            flux_app_id: pageData?.flux_app_id || '',
            // Linked Pixels - preserve existing settings
            linked_pixels: pageData?.linked_pixels || []
          };

      let deployPageId = pageId;

      // If new page, create first
      if (!deployPageId) {
        const createResponse = await landingPageService.createLandingPage(saveData);
        if (!createResponse.success || !createResponse.data) {
          return { url: '', success: false, error: createResponse.message || 'Erro ao salvar página' };
        }
        deployPageId = createResponse.data.id;
        setPageId(deployPageId);
        window.history.replaceState(null, '', `/landing-pages/${deployPageId}/edit`);
      } else {
        // Save latest changes before deploying
        await landingPageService.updateLandingPage(deployPageId, saveData);
      }

      // Deploy
      const deployResponse = await landingPageService.deployLandingPage(deployPageId);
      if (deployResponse.success && deployResponse.data) {
        const publishedUrl = deployResponse.data.url;
        // last_deployed_at atualizado aqui também: é o que apaga o aviso de
        // "pixels ainda não estão no ar" assim que a publicação conclui.
        const publishedAt = new Date().toISOString();
        setPageData(prev => prev
          ? { ...prev, published_url: publishedUrl, status: 'published', last_deployed_at: publishedAt, updated_at: publishedAt }
          : { published_url: publishedUrl, status: 'published', last_deployed_at: publishedAt, updated_at: publishedAt });
        return { url: publishedUrl, success: true };
      } else {
        return { url: '', success: false, error: deployResponse.message || 'Erro ao publicar' };
      }
    } catch (error: any) {
      console.error('Publish error:', error);
      return { url: '', success: false, error: error.message || 'Erro ao publicar' };
    } finally {
      setIsPublishing(false);
    }
  }, [pageName, pageSlug, pageId, pageData, customCode, isPublishing, isImportedHtml, importedHtml, importedCss, fullHtml, currentFullHtml, isPageLoading, confirmIfEmptyOverwrite, sanitizedIdentity]);

  // Preview handler
  const handlePreview = useCallback(() => {
    if (isImportedHtml) {
      // Imported HTML — publica/pré-visualiza o documento COMPLETO verbatim
      // (sem injeção de Tailwind/Inter) para preservar o design original.
      const docHtml = fullHtml || importedHtml;
      const blob = new Blob([docHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      return;
    }
    if (!editorRef.current) return;

    const editor = editorRef.current;
    const html = editor.getHtml();
    let css = editor.getCss() || '';

    // Get wrapper (body) styles - GrapesJS doesn't include these by default
    const wrapper = editor.getWrapper();
    if (wrapper) {
      const wrapperStyle = wrapper.getStyle();
      if (wrapperStyle && Object.keys(wrapperStyle).length > 0) {
        const wrapperCss = Object.entries(wrapperStyle)
          .map(([prop, val]) => `${prop}: ${val};`)
          .join(' ');
        css = `body { ${wrapperCss} }\n${css}`;
      }
    }

    const previewDoc = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pageName} - Preview</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    body { font-family: 'Inter', sans-serif; margin: 0; padding: 0; }
    [data-href], [data-action] { cursor: pointer; transition: opacity 0.2s; }
    [data-href]:hover, [data-action]:hover { opacity: 0.85; }
    ${css}
  </style>
</head>
<body>
  ${html}
  <script>
    document.addEventListener('click', function(e) {
      var tag = e.target.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      var el = e.target.closest('[data-action]') || e.target.closest('[data-href]');
      if (!el) return;
      if (el.closest('form') && (tag === 'button' || tag === 'submit')) return;
      e.preventDefault();
      var action = el.getAttribute('data-action') || 'link';
      switch(action) {
        case 'scroll':
          var sectionId = el.getAttribute('data-scroll-to');
          if (sectionId) {
            var target = document.getElementById(sectionId);
            if (target) target.scrollIntoView({ behavior: 'smooth' });
          }
          break;
        case 'form':
          var form = el.closest('form') || document.querySelector('form');
          if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          break;
        case 'whatsapp':
          var phone = el.getAttribute('data-whatsapp') || '';
          var msg = el.getAttribute('data-whatsapp-msg') || '';
          var wpUrl = 'https://wa.me/' + phone.replace(/[^0-9]/g, '');
          if (msg) wpUrl += '?text=' + encodeURIComponent(msg);
          window.open(wpUrl, '_blank');
          break;
        case 'phone':
          var phoneNum = el.getAttribute('data-phone') || '';
          if (phoneNum) window.location.href = 'tel:' + phoneNum.replace(/[^0-9+]/g, '');
          break;
        case 'email':
          var email = el.getAttribute('data-email') || '';
          var subject = el.getAttribute('data-email-subject') || '';
          if (email) window.location.href = 'mailto:' + email + (subject ? '?subject=' + encodeURIComponent(subject) : '');
          break;
        default:
          var href = el.getAttribute('data-href');
          var tgt = el.getAttribute('data-target');
          if (href) {
            if (tgt === '_blank') window.open(href, '_blank');
            else window.location.href = href;
          }
          break;
      }
    });
  </script>
</body>
</html>`;

    const blob = new Blob([previewDoc], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }, [pageName, isImportedHtml, fullHtml, importedHtml]);

  // Handle image upload to S3
  // Carrega o banco de imagens do usuário (S3, mais recentes primeiro).
  const loadImageLibrary = useCallback(async () => {
    setIsLoadingLibrary(true);
    setLibraryError(false);
    try {
      // Sem limite explícito o servidor devolvia 60 e a UI mostrava "Suas imagens
      // (60)" como se fosse tudo — o resto ficava invisível, sem paginação.
      const res = await api.get('/upload/images', { params: { limit: 200 } });
      if (res.data?.success) {
        setImageLibrary(res.data.data || []);
        setLibraryTotal(typeof res.data.total === 'number' ? res.data.total : (res.data.data || []).length);
      } else setLibraryError(true);
    } catch (e) {
      // Erro de rede/servidor: precisa ficar claro que é falha, e não que ela
      // "não tem imagens" (senão o cliente sobe tudo de novo à toa).
      console.error('[image-library] falha ao listar:', e);
      setLibraryError(true);
    } finally {
      setIsLoadingLibrary(false);
    }
  }, []);

  // Abriu o modal de imagem → busca a biblioteca.
  useEffect(() => {
    if (showImageModal) loadImageLibrary();
  }, [showImageModal, loadImageLibrary]);

  // Fechar sempre pelo mesmo caminho: além de limpar o estado, invalida a rodada
  // para um upload lento não aplicar a imagem depois que o usuário desistiu.
  const fecharModalImagem = useCallback(() => {
    imageModalSeqRef.current += 1;
    setShowImageModal(false);
    setSelectedImageComponent(null);
    setNewImageUrl('');
  }, []);

  // ESC fecha o modal (o de imagem só saía pelo botão Cancelar).
  useEffect(() => {
    if (!showImageModal) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && !showImageStudio) fecharModalImagem();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showImageModal, showImageStudio, fecharModalImagem]);

  // Limpar o input é obrigatório em TODO caminho de saída: sem isso, escolher o
  // mesmo arquivo de novo (depois de corrigi-lo) não dispara o onChange e a tela
  // parece travada.
  const resetImageInput = () => {
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tipo = (file.type || '').toLowerCase();
    // SVG executa script quando aberto pelo link direto e HEIC (padrão do iPhone)
    // não abre no Chrome: os dois subiam com "sucesso" e viravam imagem quebrada.
    if (/svg/.test(tipo) || /heic|heif/.test(tipo) || /\.(svg|heic|heif)$/i.test(file.name)) {
      toast.error('Formato não aceito. Envie PNG, JPG, GIF ou WEBP.');
      resetImageInput();
      return;
    }
    if (!tipo.startsWith('image/')) {
      toast.error('Esse arquivo não é uma imagem. Envie PNG, JPG, GIF ou WEBP.');
      resetImageInput();
      return;
    }
    if (file.size === 0) {
      toast.error('O arquivo está vazio (0 KB). Verifique a imagem e tente de novo.');
      resetImageInput();
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Imagem muito grande. O limite é 10MB.');
      resetImageInput();
      return;
    }

    // Rodada e alvo do momento do clique: se mudarem durante o upload, a imagem
    // não é aplicada (mas continua salva na biblioteca).
    const seq = imageModalSeqRef.current;
    const target = selectedImageComponent;
    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.url) {
        const uploadedUrl = response.data.url;

        if (seq !== imageModalSeqRef.current) {
          toast.success('Imagem enviada e guardada em "Suas imagens".');
          return;
        }

        setNewImageUrl(uploadedUrl);
        if (target) {
          if (applyImageSrc(target, uploadedUrl)) {
            toast.success('Imagem aplicada!');
            imageModalSeqRef.current += 1;
            setShowImageModal(false);
            setSelectedImageComponent(null);
            setNewImageUrl('');
          } else {
            toast.error('Não consegui aplicar a imagem neste elemento. Ela ficou salva em "Suas imagens".');
          }
        } else {
          toast.success('Upload concluído!');
        }
      } else {
        throw new Error(response.data.message || 'Erro ao fazer upload');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || error.message || 'Erro ao enviar a imagem. Tente de novo.');
    } finally {
      setIsUploadingImage(false);
      resetImageInput();
    }
  };

  // Remove uma imagem da biblioteca. O backend recusa (409) se ela estiver em uso
  // em alguma página — apagar às cegas deixava a LP publicada com buraco.
  const handleDeleteLibraryImage = async (img: { key: string; name: string }) => {
    if (!window.confirm(`Excluir "${img.name}" da sua biblioteca? Esta ação não pode ser desfeita.`)) return;
    setDeletingImageKey(img.key);
    try {
      await api.delete('/upload/images', { params: { key: img.key } });
      toast.success('Imagem excluída.');
      loadImageLibrary();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Não consegui excluir a imagem agora.');
    } finally {
      setDeletingImageKey(null);
    }
  };

  // Template selection handler
  const handleSelectTemplate = (template: PageTemplate) => {
    if (!editorRef.current) return;

    const editor = editorRef.current;

    if (template.id === 'blank') {
      editor.DomComponents.clear();
      editor.CssComposer.clear();
      setPageThumbnail(null);
      toast.success(t('pageBuilder.messages.canvasCleared'));
    } else {
      // Save template thumbnail for preview
      if (template.thumbnail) {
        setPageThumbnail(template.thumbnail);
      }
      // Clear existing content first
      editor.DomComponents.clear();
      editor.CssComposer.clear();

      // Check if template has gjsComponents (user template or full template)
      if (template.gjsComponents && template.gjsStyles) {
        try {
          editor.setComponents(JSON.parse(template.gjsComponents));
          editor.setStyle(JSON.parse(template.gjsStyles));
        } catch (e) {
          console.error('Error loading template components:', e);
          // Fallback to HTML if JSON parsing fails
          if (template.gjsHtml) {
            // Templates are already translated via getTranslatedTemplates in TemplateGallery
            editor.setComponents(template.gjsHtml);
          }
        }
      } else if (template.gjsHtml) {
        // Load from HTML (system templates) - already translated via getTranslatedTemplates
        editor.setComponents(template.gjsHtml);
        if (template.gjsCss) {
          editor.setStyle(template.gjsCss);
        }
      }
      toast.success(t('pageBuilder.messages.templateLoaded'));
    }

    setShowTemplateGallery(false);
  };

  // Save as template handler
  const handleSaveAsTemplate = useCallback(async (data: { name: string; description: string; category: string }) => {
    if (!editorRef.current) return;

    const editor = editorRef.current;

    try {
      // Capture screenshot from GrapesJS canvas
      let thumbnail: string | undefined;

      try {
        // Get the iframe body from GrapesJS
        // @ts-ignore - GrapesJS types are incomplete
        const frame = editor.Canvas?.getFrameEl?.() || document.querySelector('.gjs-frame') as HTMLIFrameElement;
        const frameDocument = frame?.contentDocument || (frame as HTMLIFrameElement)?.contentWindow?.document;
        const frameBody = frameDocument?.body;

        if (frameBody) {
          const canvas = await html2canvas(frameBody, {
            width: 1200,
            height: 900,
            scale: 0.5, // Reduce size for storage
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false
          });

          // Convert to data URL with compression
          thumbnail = canvas.toDataURL('image/jpeg', 0.7);
        }
      } catch (screenshotError) {
        console.warn('Could not capture screenshot:', screenshotError);
        // Continue without screenshot - will use placeholder
      }

      saveUserTemplate({
        name: data.name,
        description: data.description,
        category: data.category,
        gjsHtml: editor.getHtml(),
        gjsCss: editor.getCss() || '',
        gjsComponents: JSON.stringify(editor.getComponents()),
        gjsStyles: JSON.stringify(editor.getStyle()),
        thumbnail
      });

      toast.success('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error saving template');
      throw error;
    }
  }, []);

  return (
    <div className="h-screen flex flex-col bg-gray-900 overflow-hidden">
      {/* Toolbar */}
      <PageBuilderToolbar
        pageName={pageName}
        pageSlug={pageSlug}
        onPageNameChange={setPageName}
        onBack={() => navigate('/landing-pages')}
        onSave={handleSave}
        onPublish={() => setShowPublishModal(true)}
        onPreview={handlePreview}
        onSaveAsTemplate={() => setShowSaveTemplateModal(true)}
        onCustomCode={() => setShowCustomCodeModal(true)}
        onSettings={() => setShowSettingsModal(true)}
        onEditFullHtml={isImportedHtml ? () => { setFullHtml(currentFullHtml()); setShowFullHtmlModal(true); } : undefined}
        onAIEdit={isImportedHtml ? () => { setFullHtml(currentFullHtml()); setShowAIEditChat(true); } : undefined}
        editor={editorRef.current}
        isSaving={isSaving}
        isPublishing={isPublishing}
        isPageLoading={isPageLoading}
        activeDevice={activeDevice}
        onDeviceChange={setActiveDevice}
        onAutoResponsive={handleAutoResponsive}
        onUndoResponsive={handleUndoResponsive}
        hasResponsiveFix={responsiveFixApplied}
        isFixingResponsive={isFixingResponsive}
      />

      {/* Aviso: formulário externo que não funciona depois de publicar */}
      {externalFormWarning && (
        <div
          data-testid="external-form-warning"
          className="flex items-start gap-3 px-4 py-3 bg-amber-500/10 border-b border-amber-500/40 text-amber-100"
        >
          <span className="text-lg leading-none mt-0.5">⚠️</span>
          <div className="flex-1 min-w-0 text-sm">
            {externalFormWarning.reason === 'empty-form' ? (
              <>
                <p className="font-semibold text-amber-200">
                  Existe um formulário SEM NENHUM CAMPO nesta página.
                </p>
                <p className="text-amber-100/80">
                  Publicado, ele aparece como uma caixa vazia e ninguém consegue se inscrever.
                  Troque pelo formulário da DevSkin (nome, e-mail e WhatsApp) — os contatos caem direto no seu CRM e nos fluxos.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold text-amber-200">
                  O formulário do {externalFormWarning.provider} não vai funcionar na página publicada.
                </p>
                <p className="text-amber-100/80">
                  Ele só aparece completo dentro do {externalFormWarning.provider} — aqui chega sem os campos, e os leads se perdem.
                  Troque pelo formulário da DevSkin: os contatos caem direto no seu CRM e nos fluxos.
                </p>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleInsertDevskinForm}
              data-testid="insert-devskin-form"
              className="px-3 py-1.5 rounded-lg bg-amber-500 text-gray-900 text-xs font-bold hover:bg-amber-400 transition-colors"
            >
              Usar formulário da DevSkin
            </button>
            <button
              onClick={() => { setExternalFormWarning(null); setExternalFormDismissed(true); }}
              className="p-1.5 rounded-lg text-amber-200/70 hover:text-white hover:bg-amber-500/20 transition-colors"
              title="Fechar aviso"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Overlay backdrop - mobile only */}
        {!isDesktop && (showLeftSidebar || showRightSidebar) && (
          <div
            className="fixed inset-0 bg-black/40 z-20"
            onClick={() => { setShowLeftSidebar(false); setShowRightSidebar(false); }}
          />
        )}

        {/* Left Sidebar Toggle Button - mobile: always visible; desktop: only when collapsed */}
        {(!isDesktop || !showLeftSidebar) && (
          <button
            onClick={() => {
              setShowLeftSidebar(!showLeftSidebar);
              if (!isDesktop) setShowRightSidebar(false);
            }}
            className={`fixed left-0 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-r-lg shadow-lg transition-all ${
              showLeftSidebar
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 border border-gray-700 border-l-0'
            }`}
            title="Blocos / Camadas"
          >
            <PanelLeftOpen className="w-5 h-5" />
          </button>
        )}

        {/* Left Sidebar - Blocks/Layers */}
        <aside className={
          isDesktop
            ? `shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col overflow-hidden transition-all duration-300 ${showLeftSidebar ? 'w-80' : 'w-0 border-r-0'}`
            : `fixed inset-y-0 left-0 top-14 z-30 w-80 bg-gray-900 border-r border-gray-800 flex flex-col overflow-hidden transition-transform duration-300 shadow-2xl ${showLeftSidebar ? 'translate-x-0' : '-translate-x-full'}`
        }>
          {/* Sidebar Header with Close */}
          <div className="flex items-center justify-between p-3 border-b border-gray-800 bg-gray-900/80">
            <span className="text-sm font-semibold text-white">{t('pageBuilder.panels.blocks')}</span>
            <button
              onClick={() => setShowLeftSidebar(false)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* AI Banner */}
          <div className="p-3 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-b border-gray-800">
            <button
              onClick={() => { setShowAIAssistant(true); if (!isDesktop) setShowLeftSidebar(false); }}
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/20"
            >
              <Sparkles className="w-5 h-5" />
              {t('pageBuilder.ai.title')}
            </button>
          </div>

          {/* Panel Tabs */}
          <div className="flex border-b border-gray-800 bg-gray-900/50">
            <button
              onClick={() => setLeftPanel('blocks')}
              className={`flex-1 px-4 py-3.5 text-sm font-semibold transition-all ${
                leftPanel === 'blocks'
                  ? 'text-white bg-gray-800/50 border-b-2 border-indigo-500'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
            >
              {t('pageBuilder.panels.blocks')}
            </button>
            <button
              onClick={() => setLeftPanel('layers')}
              className={`flex-1 px-4 py-3.5 text-sm font-semibold transition-all ${
                leftPanel === 'layers'
                  ? 'text-white bg-gray-800/50 border-b-2 border-indigo-500'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
            >
              {t('pageBuilder.panels.layers')}
            </button>
          </div>

          {/* Block search — visible only on the blocks tab; filters by label */}
          {leftPanel === 'blocks' && (
            <div className="px-3 pt-3 pb-2 bg-gray-900/30">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  value={blockSearch}
                  onChange={(e) => setBlockSearch(e.target.value)}
                  placeholder={t('pageBuilder.blocks.searchPlaceholder', 'Buscar elementos...') as string}
                  className="w-full pl-9 pr-9 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {blockSearch && (
                  <button
                    type="button"
                    onClick={() => setBlockSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
                    aria-label="Clear"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto custom-scrollbar" style={{ position: 'relative' }}>
            <div
              id="gjs-blocks"
              className="p-3"
              style={{
                display: leftPanel === 'blocks' ? 'flex' : 'none',
                flexDirection: 'column',
                gap: '6px'
              }}
            />
            {/* Camadas: a árvore da página, com nomes em português. A dica curta
                fica acima porque o cliente não sabia que dava para clicar/arrastar. */}
            <div
              id="gjs-layers"
              className="p-3"
              style={{ display: leftPanel === 'layers' ? 'block' : 'none' }}
            >
              <p className="text-gray-500 text-xs mb-2 leading-snug">
                {t(
                  'pageBuilder.messages.layersHint',
                  'Clique em uma camada para selecionar a parte da página. Arraste para mudar a ordem.'
                )}
              </p>
            </div>
          </div>

          {/* Template Button */}
          <div className="p-3 border-t border-gray-800 bg-gray-900/50">
            <button
              onClick={() => { setShowTemplateGallery(true); if (!isDesktop) setShowLeftSidebar(false); }}
              className="w-full py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white font-medium rounded-lg transition-colors border border-gray-700"
            >
              {t('pageBuilder.templates.useTemplate')}
            </button>
          </div>
        </aside>

        {/* Canvas - Fullscreen. Páginas HTML também são editadas no GrapesJS
            (o corpo é carregado no canvas); o iframe estático foi removido. */}
        <main className="flex-1 overflow-hidden relative">
          <div
            ref={containerRef}
            id="gjs-editor"
            className="h-full"
          />
        </main>

        {/* Right Sidebar Toggle Button - mobile: always visible; desktop: only when collapsed */}
        {(!isDesktop || !showRightSidebar) && (
          <button
            onClick={() => {
              setShowRightSidebar(!showRightSidebar);
              if (!isDesktop) setShowLeftSidebar(false);
            }}
            className={`fixed right-0 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-l-lg shadow-lg transition-all ${
              showRightSidebar
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 border border-gray-700 border-r-0'
            }`}
            title="Estilos / Configuracoes"
          >
            <PanelRightOpen className="w-5 h-5" />
          </button>
        )}

        {/* Right Sidebar - Styles/Traits */}
        <aside className={
          isDesktop
            ? `shrink-0 bg-gray-900 border-l border-gray-800 flex flex-col min-h-0 transition-all duration-300 ${showRightSidebar ? 'w-80' : 'w-0 border-l-0'}`
            : `fixed inset-y-0 right-0 top-14 z-30 w-80 bg-gray-900 border-l border-gray-800 flex flex-col h-[calc(100%-3.5rem)] min-h-0 transition-transform duration-300 shadow-2xl ${showRightSidebar ? 'translate-x-0' : 'translate-x-full'}`
        }>
          {/* Sidebar Header with Close */}
          <div className="flex items-center justify-between p-3 border-b border-gray-800 bg-gray-900/80 flex-shrink-0">
            <span className="text-sm font-semibold text-white">{t('pageBuilder.panels.styles')}</span>
            <button
              onClick={() => setShowRightSidebar(false)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Panel Tabs */}
          <div className="flex border-b border-gray-800 bg-gray-900/50 flex-shrink-0">
            <button
              onClick={() => setRightPanel('styles')}
              className={`flex-1 px-4 py-3.5 text-sm font-semibold transition-all ${
                rightPanel === 'styles'
                  ? 'text-white bg-gray-800/50 border-b-2 border-indigo-500'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
            >
              {t('pageBuilder.panels.styles')}
            </button>
            <button
              onClick={() => setRightPanel('traits')}
              className={`flex-1 px-4 py-3.5 text-sm font-semibold transition-all ${
                rightPanel === 'traits'
                  ? 'text-white bg-gray-800/50 border-b-2 border-indigo-500'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
            >
              {t('pageBuilder.panels.settings')}
            </button>
          </div>

          {/* Style/Trait Manager Content */}
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
            <div
              id="gjs-sm"
              className={rightPanel === 'styles' ? 'panel-visible' : 'panel-hidden'}
            />
            <div
              id="gjs-traits"
              className={rightPanel === 'traits' ? 'panel-visible' : 'panel-hidden'}
            />
          </div>
        </aside>
      </div>

      {/* Publish Modal */}
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        pageId={pageId || ''}
        pageName={pageName}
        pageSlug={pageSlug}
        onSlugChange={setPageSlug}
        onPublish={handlePublish}
        existingUrl={pageData?.published_url}
        fluxEnabled={pageData?.flux_enabled || false}
      />

      {/* Template Gallery */}
      <TemplateGallery
        isOpen={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      {/* AI Assistant */}
      <AIAssistant
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
        editor={editorRef.current}
        pageName={pageName}
        onApplyAsHtml={(html) => {
          // A IA gerou um documento HTML completo → vira uma PÁGINA HTML,
          // editável pelo chat "Editar com IA", pelo HTML cru e publicada verbatim.
          // Se a página atual tem conteúdo montado no editor visual (GrapesJS),
          // converter para HTML é um caminho sem volta — confirma antes.
          let hasComponents = false;
          try {
            const comps = editorRef.current?.getComponents?.();
            hasComponents = !!comps && comps.length > 0;
          } catch { /* editor não pronto */ }
          if (hasComponents && !isImportedHtml) {
            const ok = window.confirm(t(
              'pageBuilder.aiEdit.convertConfirm',
              'Isso vai substituir a página atual pelo HTML gerado pela IA. Você perde a edição visual (blocos) desta página. Continuar?'
            ) as string);
            if (!ok) return;
          }
          setIsImportedHtml(true);
          setImportedHtml(html);
          setFullHtml(html);
          setHtmlUndoStack([]);
          loadHtmlIntoEditor(html); // carrega no editor visual para edição
        }}
      />

      {/* Save Template Modal */}
      <SaveTemplateModal
        isOpen={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
        onSave={handleSaveAsTemplate}
      />

      {/* Editor de HTML completo — páginas importadas via HTML */}
      <FullHtmlModal
        isOpen={showFullHtmlModal}
        onClose={() => setShowFullHtmlModal(false)}
        html={fullHtml}
        onSave={(html) => {
          // Empilha o HTML anterior no histórico e recarrega o novo no editor visual.
          setHtmlUndoStack(prev => [...prev, currentFullHtml()]);
          setFullHtml(html);
          loadHtmlIntoEditor(html);
          setShowFullHtmlModal(false);
          toast.success(t('pageBuilder.fullHtml.applied', 'Aplicado!'));
        }}
      />

      {/* Chat "Editar com IA" — páginas HTML (geradas por IA / importadas) */}
      {isImportedHtml && (
        <AIEditChat
          isOpen={showAIEditChat}
          onClose={() => setShowAIEditChat(false)}
          currentHtml={fullHtml || importedHtml}
          getCurrentHtml={currentFullHtml}
          canUndo={htmlUndoStack.length > 0}
          onApply={(newHtml) => {
            // empilha o HTML ATUAL (do editor) para o Desfazer e aplica o novo
            setHtmlUndoStack(prev => [...prev, currentFullHtml()]);
            setFullHtml(newHtml);
            loadHtmlIntoEditor(newHtml); // reflete no editor visual
          }}
          onUndo={() => {
            // Fora do updater: setState puro (evita toast/efeitos duplicados no StrictMode).
            if (htmlUndoStack.length === 0) return;
            const previous = htmlUndoStack[htmlUndoStack.length - 1];
            setHtmlUndoStack(prev => prev.slice(0, -1));
            setFullHtml(previous);
            loadHtmlIntoEditor(previous); // reflete no editor visual
            toast.success(t('pageBuilder.aiEdit.undone', 'Alteração desfeita.'));
          }}
        />
      )}

      {/* Custom Code Modal */}
      <CustomCodeModal
        isOpen={showCustomCodeModal}
        onClose={() => setShowCustomCodeModal(false)}
        headCode={customCode.headCode}
        bodyStartCode={customCode.bodyStartCode}
        bodyEndCode={customCode.bodyEndCode}
        customCss={customCode.customCss}
        onSave={async (codes) => {
          // Update local state
          setCustomCode(codes);

          // Apply custom CSS to GrapesJS editor
          if (editorRef.current && codes.customCss) {
            const existingCss = editorRef.current.getCss() || '';
            if (!existingCss.includes('/* CUSTOM USER CSS */')) {
              editorRef.current.setStyle(existingCss + '\n/* CUSTOM USER CSS */\n' + codes.customCss);
            }
          }

          // Auto-save to backend
          if (!editorRef.current) {
            toast.error('Editor not initialized');
            return;
          }

          const editor = editorRef.current;

          // Generate unique slug for new pages
          const uniqueSlug = pageId ? pageSlug : `${pageSlug}-${Date.now()}`;

          const saveData = {
            name: pageName || 'Nova Landing Page',
            slug: uniqueSlug,
            gjs_html: editor.getHtml(),
            gjs_css: editor.getCss() || '',
            gjs_components: JSON.stringify(editor.getComponents()),
            gjs_styles: JSON.stringify(editor.getStyle()),
            head_code: codes.headCode,
            body_start_code: codes.bodyStartCode,
            body_end_code: codes.bodyEndCode,
            custom_css: codes.customCss
          };

          try {
            if (pageId) {
              // Update existing page
              const response = await landingPageService.updateLandingPage(pageId, saveData);
              if (response.success) {
                toast.success('Code saved!');
              } else {
                throw new Error(response.message);
              }
            } else {
              // Create new page first
              const response = await landingPageService.createLandingPage(saveData);
              if (response.success && response.data) {
                setPageId(response.data.id);
                setPageSlug(response.data.slug);
                window.history.replaceState(null, '', `/landing-pages/${response.data.id}/edit`);
                toast.success('Page created and code saved!');
              } else {
                throw new Error(response.message || 'Erro ao criar página');
              }
            }
          } catch (error: any) {
            console.error('Error saving:', error);
            toast.error(error.message || 'Error saving');
          }
        }}
      />

      {/* Image Change Modal */}
      {showImageModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => { if (!isUploadingImage) fecharModalImagem(); }}
        >
          <div
            className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl mx-4 shadow-2xl border border-gray-700 max-h-[88vh] overflow-y-auto custom-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold text-white mb-4">🖼️ Trocar Imagem</h3>
            <div className="space-y-4">
              {/* Upload de arquivo */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fazer upload de imagem
                </label>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload-input"
                />
                <button
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isUploadingImage ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Enviando...
                    </>
                  ) : (
                    <>📤 Escolher arquivo do computador</>
                  )}
                </button>
              </div>

              {/* Image Studio */}
              <button
                onClick={() => setShowImageStudio(true)}
                className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                🎨 Image Studio
              </button>

              {/* Banco de imagens — tudo que já foi enviado fica guardado aqui.
                  Resolve o "subi a imagem e ela sumiu, tive que subir de novo"
                  e dá o link pronto para reusar em HTML/anúncios. */}
              <div data-testid="image-library">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Suas imagens ({libraryTotal > imageLibrary.length
                      ? `${imageLibrary.length} de ${libraryTotal}`
                      : imageLibrary.length})
                  </label>
                  <button
                    onClick={loadImageLibrary}
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    Atualizar
                  </button>
                </div>
                {isLoadingLibrary ? (
                  <p className="text-sm text-gray-500 py-4 text-center">Carregando suas imagens...</p>
                ) : libraryError ? (
                  <div className="text-sm text-amber-300/90 py-3 px-3 text-center border border-amber-500/40 rounded-lg bg-amber-500/10">
                    Não consegui carregar suas imagens agora. Suas imagens continuam salvas —
                    clique em <button onClick={loadImageLibrary} className="underline font-semibold">tentar de novo</button>.
                  </div>
                ) : imageLibrary.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center border border-dashed border-gray-600 rounded-lg">
                    Nenhuma imagem ainda. As que você enviar ficam salvas aqui para reusar.
                  </p>
                ) : (
                  <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto custom-scrollbar p-1">
                    {imageLibrary.map((img) => (
                      <div
                        key={img.key}
                        className={`relative group rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                          newImageUrl === img.url ? 'border-indigo-500 ring-2 ring-indigo-500/40' : 'border-gray-700 hover:border-gray-500'
                        }`}
                        onClick={() => setNewImageUrl(img.url)}
                        title={img.name}
                      >
                        <img
                          src={img.url}
                          alt={img.name}
                          className="w-full h-20 object-cover bg-gray-900"
                          loading="lazy"
                          onError={(e) => {
                            // Objeto removido/ inacessível: sem isto ficava o ícone
                            // quebrado do navegador, sem explicação nenhuma.
                            const el = e.currentTarget;
                            el.style.display = 'none';
                            const aviso = el.nextElementSibling as HTMLElement | null;
                            if (aviso) aviso.style.display = 'flex';
                          }}
                        />
                        <div
                          className="w-full h-20 bg-gray-900 text-[10px] text-gray-400 items-center justify-center text-center px-1"
                          style={{ display: 'none' }}
                        >
                          imagem indisponível
                        </div>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            // navigator.clipboard não existe fora de HTTPS e pode ser
                            // negado pelo navegador: antes dizíamos "copiado!" mesmo
                            // quando nada tinha ido para a área de transferência.
                            const copiou = await copiarTexto(img.url);
                            if (copiou) toast.success('Link da imagem copiado!');
                            else {
                              window.prompt('Copie o link da imagem (Ctrl+C / Cmd+C):', img.url);
                            }
                          }}
                          className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Copiar link da imagem"
                        >
                          copiar link
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteLibraryImage(img); }}
                          disabled={deletingImageKey === img.key}
                          className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-red-600/80 hover:bg-red-600 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-60"
                          title="Excluir imagem da biblioteca"
                        >
                          {deletingImageKey === img.key ? '...' : 'excluir'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {libraryTotal > imageLibrary.length && (
                  <p className="text-[11px] text-gray-500 mt-2">
                    Mostrando as {imageLibrary.length} mais recentes de {libraryTotal}. As demais continuam salvas.
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-gray-600"></div>
                <span className="text-gray-400 text-sm">ou</span>
                <div className="flex-1 border-t border-gray-600"></div>
              </div>

              {/* URL da imagem */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Colar URL da imagem
                </label>
                <input
                  type="text"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="https://exemplo.com/imagem.jpg"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Preview */}
              {newImageUrl && (
                <div className="border border-gray-600 rounded-lg p-3 bg-gray-900">
                  <p className="text-xs text-gray-400 mb-2">Preview:</p>
                  <div className="flex items-center justify-center min-h-[100px]">
                    <img
                      key={newImageUrl}
                      src={newImageUrl}
                      alt="Preview"
                      className="max-h-40 max-w-full rounded"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 break-all">{newImageUrl}</p>
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={fecharModalImagem}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    const url = newImageUrl.trim();
                    // Campo livre: dava para escrever "minha logo" e a página ia
                    // para o ar com <img src="minha logo"> depois de um "sucesso".
                    if (!/^(https?:\/\/|data:image\/|\/)/i.test(url)) {
                      toast.error('Link inválido. Cole um endereço que comece com https:// ou escolha uma imagem acima.');
                      return;
                    }
                    if (selectedImageComponent) {
                      if (!applyImageSrc(selectedImageComponent, url)) {
                        toast.error('Não consegui aplicar a imagem neste elemento.');
                        return;
                      }
                      toast.success('Imagem atualizada!');
                    }
                    fecharModalImagem();
                  }}
                  disabled={!newImageUrl.trim()}
                  className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating AI Button */}
      {!showAIAssistant && (
        <button
          onClick={() => setShowAIAssistant(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-full shadow-lg shadow-indigo-500/30 flex items-center justify-center transition-all hover:scale-110 z-40"
          title="Assistente IA"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {/* Page Settings Modal */}
      <PageSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        settings={{
          name: pageName,
          slug: pageSlug,
          meta_title: pageData?.meta_title || '',
          meta_description: pageData?.meta_description || '',
          meta_keywords: pageData?.meta_keywords || '',
          og_image: pageData?.og_image || '',
          favicon: pageData?.favicon || '',
          flux_enabled: pageData?.flux_enabled || false,
          flux_api_key: pageData?.flux_api_key || '',
          flux_app_id: pageData?.flux_app_id || '',
          linked_pixels: pageData?.linked_pixels || []
        }}
        publishInfo={{
          status: pageData?.status,
          updatedAt: pageData?.updated_at || null,
          lastDeployedAt: pageData?.last_deployed_at || null,
        }}
        onSave={async (settings) => {
          // Update local state
          setPageName(settings.name);
          setPageSlug(settings.slug);
          setPageData(prev => ({
            ...prev,
            meta_title: settings.meta_title,
            meta_description: settings.meta_description,
            meta_keywords: settings.meta_keywords,
            og_image: settings.og_image,
            favicon: settings.favicon,
            flux_enabled: settings.flux_enabled,
            flux_api_key: settings.flux_api_key,
            flux_app_id: settings.flux_app_id,
            linked_pixels: settings.linked_pixels,
            // Marca a alteração agora: é isso que faz o aviso "pixels ainda não
            // estão no ar" aparecer imediatamente depois de salvar, sem recarregar.
            updated_at: new Date().toISOString()
          }));

          // Auto-save to backend
          if (pageId) {
            try {
              await landingPageService.updateLandingPage(pageId, {
                name: settings.name,
                slug: settings.slug,
                meta_title: settings.meta_title,
                meta_description: settings.meta_description,
                meta_keywords: settings.meta_keywords,
                og_image: settings.og_image,
                favicon: settings.favicon,
                linked_pixels: settings.linked_pixels,
                flux_enabled: settings.flux_enabled,
                flux_api_key: settings.flux_api_key,
                flux_app_id: settings.flux_app_id
              });
              toast.success(t('pageBuilder.settings.saved', 'Settings saved!'));
            } catch (error) {
              console.error('Error saving settings:', error);
              toast.error(t('pageBuilder.settings.error', 'Error saving settings'));
            }
          } else {
            toast.success(t('pageBuilder.settings.savedLocal', 'Settings applied! Save the page to persist.'));
          }
        }}
      />

      {/* Custom Styles for GrapesJS */}
      <style>{`
        /* Panel visibility - high specificity */
        aside #gjs-blocks.panel-hidden,
        aside #gjs-layers.panel-hidden,
        aside #gjs-sm.panel-hidden,
        aside #gjs-traits.panel-hidden,
        .panel-hidden {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          overflow: hidden !important;
        }
        aside #gjs-blocks.panel-visible {
          display: flex !important;
          flex-direction: column !important;
          gap: 6px !important;
          visibility: visible !important;
          height: auto !important;
        }
        aside #gjs-layers.panel-visible,
        aside #gjs-sm.panel-visible,
        aside #gjs-traits.panel-visible,
        .panel-visible {
          display: block !important;
          visibility: visible !important;
          height: auto !important;
        }

        /* Custom Scrollbar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }

        /* GrapesJS Theme Overrides */
        .gjs-one-bg {
          background-color: #111827 !important;
        }
        .gjs-two-color {
          color: #e5e7eb !important;
        }
        .gjs-three-bg {
          background-color: #1f2937 !important;
        }
        .gjs-four-color,
        .gjs-four-color-h:hover {
          color: #818cf8 !important;
        }

        /* Hide GrapesJS default panels */
        .gjs-pn-panels {
          display: none !important;
        }

        /* Ensure editor wrapper fills container */
        .gjs-editor-cont {
          width: 100% !important;
          height: 100% !important;
          position: relative !important;
        }

        /* Editor Container */
        #gjs-editor {
          width: 100% !important;
          height: 100% !important;
        }

        .gjs-editor {
          width: 100% !important;
          height: 100% !important;
        }

        /* Canvas */
        .gjs-cv-canvas {
          background-color: #1e293b !important;
          width: 100% !important;
          height: 100% !important;
          /* o GrapesJS reserva 40px em cima pra barra dele (que não usamos): virava uma faixa preta vazia */
          top: 0 !important;
        }

        .gjs-cv-canvas__frames {
          overflow: auto !important;
          padding: 30px !important;
          padding-bottom: 300px !important;
          min-height: 100% !important;
        }

        .gjs-frame-wrapper {
          background-color: white;
          box-shadow: 0 0 60px rgba(0,0,0,0.4);
          margin: 0 auto !important;
          margin-bottom: 200px !important;
          border-radius: 12px;
          overflow: visible !important;
          min-height: 500px !important;
        }

        .gjs-frame {
          background: white !important;
          min-height: 100% !important;
        }

        /* Blocks Container - display controlled by React inline style */
        #gjs-blocks {
          flex-direction: column;
          gap: 6px;
        }

        /* Block Categories */
        .gjs-block-category {
          margin-bottom: 8px !important;
        }
        .gjs-block-category .gjs-title {
          background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%) !important;
          border: none !important;
          border-radius: 10px !important;
          padding: 12px 16px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #c7d2fe !important;
          text-transform: uppercase !important;
          letter-spacing: 0.8px !important;
          cursor: pointer !important;
          margin-bottom: 8px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
        }
        .gjs-block-category .gjs-title:hover {
          background: linear-gradient(135deg, #312e81 0%, #4c1d95 100%) !important;
        }
        .gjs-block-category .gjs-caret-icon {
          color: #a5b4fc !important;
          font-size: 10px !important;
          transition: transform 0.2s ease !important;
        }
        .gjs-block-category.gjs-open .gjs-caret-icon {
          transform: rotate(90deg) !important;
        }
        .gjs-block-category .gjs-blocks-c {
          flex-direction: column;
          gap: 6px;
          padding: 0 !important;
          margin-bottom: 8px !important;
        }
        .gjs-block-category.gjs-open .gjs-blocks-c {
          display: flex !important;
        }
        .gjs-block-category:not(.gjs-open) .gjs-blocks-c {
          display: none !important;
        }

        /* Individual Blocks - Full Width */
        .gjs-block {
          width: 100% !important;
          min-height: 56px !important;
          padding: 12px 16px !important;
          border-radius: 10px !important;
          border: 1px solid #374151 !important;
          background: linear-gradient(145deg, #1f2937 0%, #111827 100%) !important;
          margin: 0 !important;
          transition: all 0.2s ease !important;
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          justify-content: flex-start !important;
          gap: 12px !important;
          cursor: grab !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.15) !important;
        }
        .gjs-block:hover {
          border-color: #6366f1 !important;
          background: linear-gradient(145deg, #1e1b4b 0%, #312e81 100%) !important;
          transform: translateX(4px) !important;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2) !important;
        }
        .gjs-block:active {
          cursor: grabbing !important;
          transform: scale(0.98) !important;
        }
        .gjs-block__media {
          margin: 0 !important;
          width: 36px !important;
          height: 36px !important;
          min-width: 36px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: #818cf8 !important;
          background: rgba(99, 102, 241, 0.15) !important;
          border-radius: 8px !important;
          padding: 6px !important;
        }
        .gjs-block__media svg {
          width: 20px !important;
          height: 20px !important;
        }
        .gjs-block-label {
          font-size: 13px !important;
          font-weight: 500 !important;
          color: #d1d5db !important;
          text-align: left !important;
          line-height: 1.3 !important;
        }
        .gjs-block:hover .gjs-block-label {
          color: #ffffff !important;
        }
        .gjs-block:hover .gjs-block__media {
          background: rgba(99, 102, 241, 0.3) !important;
          color: #a5b4fc !important;
        }

        /* ============================================
           STYLE MANAGER + TRAITS — Dark Theme
           Based on native GrapesJS structure:
           - Properties use float:left width:50% (2-col grid)
           - --full properties span 100%
           - .gjs-label-wrp is 30% width for inline labels
           - Traits use display:flex with gap
           ============================================ */

        /* Dark color scheme for native form elements (macOS) */
        #gjs-sm, #gjs-traits { color-scheme: dark !important; }

        /* ====== STYLE MANAGER ====== */
        #gjs-sm {
          overflow-y: auto !important;
          font-size: 13px !important;
        }

        /* — Sectors — */
        .gjs-sm-sector {
          clear: both !important;
          border-bottom: 1px solid #1e293b !important;
        }
        .gjs-sm-sector-title {
          background-color: #0f172a !important;
          border: none !important;
          border-bottom: 1px solid #1e293b !important;
          padding: 10px 14px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #94a3b8 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.8px !important;
          cursor: pointer !important;
        }
        .gjs-sm-sector-title:hover {
          background-color: #1e293b !important;
          color: #cbd5e1 !important;
        }
        .gjs-sm-sector.gjs-sm-open .gjs-sm-sector-title {
          color: #a5b4fc !important;
          border-left: 3px solid #6366f1 !important;
        }
        .gjs-sm-sector-caret {
          color: #64748b !important;
        }
        .gjs-sm-sector.gjs-sm-open .gjs-sm-sector-caret {
          color: #818cf8 !important;
        }

        /* — Properties container — */
        .gjs-sm-properties {
          padding: 10px 10px 14px !important;
          background-color: #0f172a !important;
          overflow: hidden !important; /* clearfix for floats */
        }

        /* — Individual property (keeps GrapesJS 2-col float layout) — */
        .gjs-sm-property {
          float: left !important;
          width: 50% !important;
          padding: 4px 5px !important;
          margin-bottom: 5px !important;
          box-sizing: border-box !important;
        }
        .gjs-sm-property--full,
        .gjs-sm-property.gjs-sm-composite,
        .gjs-sm-property.gjs-sm-file,
        .gjs-sm-property.gjs-sm-list,
        .gjs-sm-property.gjs-sm-stack,
        .gjs-sm-property.gjs-sm-slider,
        .gjs-sm-property.gjs-sm-color {
          width: 100% !important;
        }

        /* — Labels — above the field, clear and readable */
        .gjs-sm-label {
          display: flex !important;
          align-items: center !important;
          color: #94a3b8 !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          margin: 0 0 5px 0 !important;
          padding: 0 !important;
          line-height: 1.3 !important;
        }
        .gjs-sm-label .gjs-sm-icon {
          display: none !important;
        }

        /* — Fields — */
        .gjs-field {
          background-color: #1e293b !important;
          border: 1px solid #334155 !important;
          border-radius: 6px !important;
          color: #e2e8f0 !important;
          font-size: 13px !important;
          min-height: 36px !important;
          transition: border-color 0.15s !important;
        }
        .gjs-field:hover {
          border-color: #475569 !important;
        }
        .gjs-field:focus-within {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 2px rgba(99,102,241,0.15) !important;
        }
        .gjs-field input,
        .gjs-field select,
        .gjs-field textarea {
          color: #e2e8f0 !important;
          font-size: 13px !important;
          padding: 7px 10px !important;
          background: transparent !important;
        }
        .gjs-field input::placeholder {
          color: #4b5563 !important;
        }
        .gjs-field select option {
          background-color: #1e293b !important;
          color: #e2e8f0 !important;
        }

        /* — Clear button (X) — hidden, clutters the UI */
        .gjs-sm-clear {
          display: none !important;
        }

        /* — Select dropdown arrow — */
        .gjs-field .gjs-sel-arrow {
          width: 9px !important;
          position: absolute !important;
          right: 0 !important;
          top: 0 !important;
          height: 100% !important;
          z-index: 0 !important;
        }
        .gjs-field .gjs-d-s-arrow {
          border-top-color: #94a3b8 !important;
        }

        /* — Number field arrows — minimal clean style */
        .gjs-field-arrows {
          background: #253044 !important;
          border-left: 1px solid #334155 !important;
        }
        .gjs-field-arrow-u, .gjs-field-arrow-d {
          border-color: #64748b !important;
          cursor: pointer !important;
        }
        .gjs-field-arrow-u:hover, .gjs-field-arrow-d:hover {
          border-color: #e2e8f0 !important;
        }

        /* Units: disable dropdown, keep the selected value visible */
        .gjs-field-units {
          pointer-events: none !important;
        }
        .gjs-field-units select {
          color: #64748b !important;
          font-size: 11px !important;
          -webkit-appearance: none !important;
          appearance: none !important;
        }
        /* Hide unit dropdown arrow */
        .gjs-field-units .gjs-d-s-arrow,
        .gjs-field-units .gjs-sel-arrow {
          display: none !important;
        }

        /* — Color field — keep native GrapesJS layout, just style it */
        .gjs-field-color input {
          padding-right: 38px !important;
        }
        .gjs-field-colorp {
          border-left: 1px solid #334155 !important;
          border-radius: 0 5px 5px 0 !important;
          padding: 3px !important;
        }
        .gjs-field-colorp .gjs-checker-bg,
        .gjs-field-colorp .gjs-field-colorp-c {
          border-radius: 3px !important;
        }
        .gjs-field-color-picker {
          border-radius: 3px !important;
          cursor: pointer !important;
        }

        /* — Composite (e.g. border-radius 4 corners) — */
        .gjs-sm-composite {
          background: #1e293b !important;
          border-radius: 8px !important;
          padding: 8px !important;
          border: 1px solid #334155 !important;
        }
        .gjs-sm-composite .gjs-sm-property {
          width: 50% !important;
          margin-bottom: 4px !important;
        }
        .gjs-sm-composite .gjs-sm-label {
          font-size: 10px !important;
          color: #64748b !important;
        }
        .gjs-sm-composite .gjs-field {
          min-height: 30px !important;
          font-size: 12px !important;
        }
        .gjs-sm-composite .gjs-field input {
          font-size: 12px !important;
          padding: 5px 8px !important;
        }

        /* — Slider — */
        .gjs-field-range {
          accent-color: #6366f1 !important;
        }
        .gjs-sm-slider .gjs-field {
          border: none !important;
          background: transparent !important;
          min-height: auto !important;
        }

        /* — Radio buttons (text-align, etc) — */
        .gjs-radio-items {
          display: flex !important;
          gap: 3px !important;
        }
        .gjs-radio-item {
          margin: 0 !important;
        }
        .gjs-radio-item-label {
          padding: 7px 12px !important;
          border-radius: 6px !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #94a3b8 !important;
          background: #1e293b !important;
          border: 1px solid #334155 !important;
          cursor: pointer !important;
        }
        .gjs-radio-item-label:hover {
          color: #e2e8f0 !important;
          border-color: #475569 !important;
          background: #334155 !important;
        }
        .gjs-radio-item input:checked + .gjs-radio-item-label {
          background: #6366f1 !important;
          border-color: #6366f1 !important;
          color: white !important;
        }

        /* — Color picker popup (Spectrum) — position to the left so it doesn't go off-screen */
        .sp-container {
          background-color: #0f172a !important;
          border-color: #334155 !important;
          border-radius: 10px !important;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5) !important;
          z-index: 9999 !important;
        }
        .sp-input {
          background: #1e293b !important;
          color: #f1f5f9 !important;
          border-color: #334155 !important;
          border-radius: 6px !important;
        }

        /* — Gradient editor panel — prevent SM float from affecting children */
        #gradient-editor-panel,
        #gradient-editor-panel > div,
        #gradient-editor-panel > div > div {
          float: none !important;
          width: auto !important;
          clear: both !important;
        }

        /* — Selector Manager — hide (we use our own) */
        #gjs-selectors { display: none !important; }

        /* — Selection outline — */
        .gjs-selected { outline: 2px solid #6366f1 !important; outline-offset: -2px; }
        .gjs-hovered { outline: 2px dashed #818cf8 !important; }

        /* ====== TRAITS PANEL ====== */
        /* GrapesJS traits: .gjs-trt-trait is already display:flex with gap.
           .gjs-label-wrp is 30% width. We keep this native layout. */
        #gjs-traits {
          padding: 0 !important;
          font-size: 13px !important;
        }
        .gjs-trt-traits {
          padding: 10px !important;
          background: #0f172a !important;
        }

        /* Each trait row */
        .gjs-trt-trait {
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          padding: 6px 4px !important;
          border-bottom: 1px solid #1e293b !important;
          font-weight: normal !important;
        }
        .gjs-trt-trait:last-child {
          border-bottom: none !important;
        }

        /* Trait label (left side, 30% native) */
        .gjs-trt-trait .gjs-label-wrp {
          width: 30% !important;
          min-width: 30% !important;
        }
        .gjs-trt-trait .gjs-label-wrp,
        .gjs-trt-trait .gjs-label {
          color: #94a3b8 !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          text-overflow: ellipsis !important;
          overflow: hidden !important;
          white-space: nowrap !important;
        }

        /* Trait field (right side, fills remaining 70%) */
        .gjs-trt-trait .gjs-field {
          flex: 1 !important;
          min-height: 36px !important;
        }
        .gjs-trt-trait .gjs-field input,
        .gjs-trt-trait .gjs-field select {
          color: #e2e8f0 !important;
          font-size: 13px !important;
          padding: 7px 10px !important;
          background: transparent !important;
        }
        .gjs-trt-trait .gjs-field input::placeholder {
          color: #4b5563 !important;
        }
        .gjs-trt-trait .gjs-field select option {
          background-color: #1e293b !important;
          color: #e2e8f0 !important;
        }
        .gjs-trt-trait .gjs-field-checkbox input {
          accent-color: #6366f1 !important;
          width: 18px !important;
          height: 18px !important;
        }

        /* ====== LAYER MANAGER ====== */
        .gjs-layers {
          background: #0f172a !important;
        }
        .gjs-layer {
          background: transparent !important;
          border-bottom: 1px solid #1e293b !important;
        }
        .gjs-layer.gjs-selected {
          background: #1e293b !important;
          outline: none !important;
        }
        .gjs-layer-title {
          padding: 10px 12px !important;
          color: #e2e8f0 !important;
          font-size: 12px !important;
        }
        .gjs-layer-title-c {
          color: #94a3b8 !important;
        }

        /* ====== SCROLLBAR ====== */
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px !important;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #0f172a !important;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #334155 !important;
          border-radius: 3px !important;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #475569 !important;
        }

        /* Layers */
        #gjs-layers {
          padding: 12px !important;
          background: #0f172a !important;
          min-height: 100px !important;
        }
        #gjs-layers .gjs-layers {
          background: transparent !important;
          padding: 0 !important;
        }
        #gjs-layers .gjs-layer {
          background-color: #1f2937 !important;
          padding: 10px 14px !important;
          border-radius: 8px !important;
          margin-bottom: 4px !important;
          border: 1px solid #374151 !important;
        }
        #gjs-layers .gjs-layer:hover {
          background-color: #374151 !important;
          border-color: #4b5563 !important;
        }
        #gjs-layers .gjs-layer.gjs-selected {
          background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%) !important;
          border-color: #6366f1 !important;
        }
        #gjs-layers .gjs-layer-title,
        #gjs-layers .gjs-layer-name {
          color: #e5e7eb !important;
          font-size: 12px !important;
        }
        #gjs-layers .gjs-layer-caret {
          color: #9ca3af !important;
          margin-right: 8px !important;
        }
        #gjs-layers .gjs-layer-count {
          color: #6b7280 !important;
          font-size: 10px !important;
        }
        #gjs-layers .gjs-layer-vis {
          color: #6b7280 !important;
        }
        #gjs-layers .gjs-layer__icon {
          color: #818cf8 !important;
          margin-right: 8px !important;
        }
        /* Hide empty message when layers exist */
        #gjs-layers .gjs-layers ~ .gjs-layers-empty,
        #gjs-layers .gjs-layer-wrapper ~ .gjs-layers-empty {
          display: none !important;
        }

        /* Traits / Configurações */
        #gjs-traits {
          padding: 16px !important;
          background: #0f172a !important;
        }
        .gjs-trt-traits {
          padding: 0 !important;
          background: transparent !important;
        }
        .gjs-trt-trait {
          padding: 12px 0 !important;
          border-bottom: 1px solid #1f2937 !important;
        }
        .gjs-trt-trait:last-child {
          border-bottom: none !important;
        }
        .gjs-trt-trait__wrp-title {
          color: #d1d5db !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          margin-bottom: 8px !important;
        }
        .gjs-trt-trait input[type="text"],
        .gjs-trt-trait input[type="number"],
        .gjs-trt-trait input[type="url"],
        .gjs-trt-trait input[type="email"],
        .gjs-trt-trait select {
          background: #1f2937 !important;
          border: 1px solid #374151 !important;
          border-radius: 8px !important;
          padding: 10px 12px !important;
          color: #f3f4f6 !important;
          font-size: 13px !important;
          width: 100% !important;
        }
        .gjs-trt-trait input[type="text"]:focus,
        .gjs-trt-trait input[type="number"]:focus,
        .gjs-trt-trait input[type="url"]:focus,
        .gjs-trt-trait input[type="email"]:focus,
        .gjs-trt-trait select:focus {
          border-color: #6366f1 !important;
          outline: none !important;
        }
        /* Checkbox styling */
        .gjs-trt-trait input[type="checkbox"] {
          width: 18px !important;
          height: 18px !important;
          padding: 0 !important;
          margin: 0 !important;
          border-radius: 4px !important;
          border: 2px solid #374151 !important;
          background: #1f2937 !important;
          cursor: pointer !important;
          accent-color: #6366f1 !important;
          vertical-align: middle !important;
        }
        .gjs-trt-trait input[type="checkbox"]:checked {
          background: #6366f1 !important;
          border-color: #6366f1 !important;
        }
        .gjs-trt-trait--checkbox {
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
        }
        .gjs-trt-trait--checkbox .gjs-trt-trait__wrp-title {
          margin-bottom: 0 !important;
          order: 2 !important;
        }
        .gjs-trt-trait--checkbox .gjs-field {
          order: 1 !important;
          width: auto !important;
        }

        /* RTE Toolbar */
        .gjs-rte-toolbar {
          background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%) !important;
          border: 1px solid #4c1d95 !important;
          border-radius: 10px !important;
          padding: 4px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.4) !important;
        }
        .gjs-rte-action {
          color: #e5e7eb !important;
          border-radius: 6px !important;
          margin: 2px !important;
        }
        .gjs-rte-action:hover {
          background-color: rgba(99, 102, 241, 0.3) !important;
          color: white !important;
        }
        .gjs-rte-active {
          background-color: #6366f1 !important;
          color: white !important;
        }

        /* Component Toolbar */
        .gjs-toolbar {
          background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%) !important;
          border-radius: 8px !important;
          padding: 4px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.4) !important;
        }
        .gjs-toolbar-item {
          color: #e5e7eb !important;
          padding: 6px !important;
          border-radius: 4px !important;
        }
        .gjs-toolbar-item:hover {
          color: white !important;
          background-color: rgba(99, 102, 241, 0.3) !important;
        }

        /* Resizer — as "alças de regulagem". Ficam grandes e bem visíveis:
           o cliente não achava os pontos para aumentar/diminuir a seção. */
        .gjs-resizer-c {
          outline: 2px dashed rgba(99, 102, 241, 0.6) !important;
          outline-offset: 0 !important;
        }
        .gjs-resizer-h {
          border: 2px solid #ffffff !important;
          background-color: #6366f1 !important;
          width: 14px !important;
          height: 14px !important;
          margin: -7px 0 0 -7px !important;
          border-radius: 4px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45) !important;
          opacity: 1 !important;
        }
        .gjs-resizer-h:hover {
          background-color: #818cf8 !important;
          transform: scale(1.15);
        }
        /* Alças do meio das laterais/base: formato de "barra", igual aos
           editores que ela já conhece (RD/Canva) — sugere arrastar. */
        .gjs-resizer-h-bc, .gjs-resizer-h-tc {
          width: 34px !important;
          margin-left: -17px !important;
          cursor: ns-resize !important;
        }
        .gjs-resizer-h-cl, .gjs-resizer-h-cr {
          height: 34px !important;
          margin-top: -17px !important;
          cursor: ew-resize !important;
        }

        /* Badge */
        .gjs-badge {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
          border-radius: 4px !important;
          font-size: 10px !important;
          padding: 2px 6px !important;
        }
      `}</style>

      {/* Image Studio Modal */}
      <ImageStudioModal
        isOpen={showImageStudio}
        onClose={() => setShowImageStudio(false)}
        onSelect={(imageUrl) => {
          setShowImageStudio(false);
          // Imagem ainda em geração não tem URL: aplicávamos string vazia, o
          // modal fechava e o toast dizia "aplicada!" sem nada ter mudado.
          if (!imageUrl || !imageUrl.trim()) {
            toast.error('Esta imagem ainda está sendo gerada. Aguarde um instante e tente de novo.');
            return;
          }
          if (selectedImageComponent) {
            if (!applyImageSrc(selectedImageComponent, imageUrl)) {
              toast.error('Não consegui aplicar a imagem neste elemento.');
              return;
            }
            toast.success('Imagem aplicada!');
            fecharModalImagem();
          } else {
            setNewImageUrl(imageUrl);
          }
        }}
        mode="all"
      />
    </div>
  );
};

export default LandingPageBuilder;
